import { useEffect, useState } from "react";
import { channels } from "../../shared/constants";
import {
		CheckCircleIcon,
		Info,
		TrashIcon,
		XCircleIcon,
		MessageSquareText,
		Clock,
		Filter,
		ChevronDown,
		Settings2,
		Bell,
		BellRing,
		Wand2,
		Brain,
		Smartphone,
		Download,
		Upload,
		Plus,
	} from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "react-toastify";


import { QRCodeDialog } from "./components/QRCodeDialog";
import { ChangePasswordModal } from "./components/ChangePasswordDialog";
import { GeminiSettingsDialog } from "./components/GeminiSettingsDialog";
import { GPTSettingsDialog } from "./components/GPTSettingsDialog";
import { RefreshCwIcon } from "lucide-react";
import { OzapAISettingsDialog } from "./components/OzapAISettingsDialog";
import { ClaudeSettingsDialog } from "./components/ClaudeSettingsDialog";
import { MessageTimerDialog } from "./components/MessageTimerDialog";
import { FollowUpPromptDialog } from "./components/FollowUpPromptDialog";
import { SmartAudioDialog, ElevenLabsDialog } from "./components/AudioSettingsDialogs";
import { ImportNumbersDialog } from "./components/ImportNumbersDialog";
import TTSSection from "./components/TTSSection";
import AIMediaSection from "./components/AIMediaSection";
import APIFunctionsSection from "./components/APIFunctionsSection";
import GoogleCalendarSection from "./components/GoogleCalendarSection";
import GroupsSection from "./components/GroupsSection";
import GroupSchedulerSection from "./components/GroupSchedulerSection";

const { ipcRenderer } = window.require("electron");

/**
 * @param {Object} props - The component props.
 */
export const Dashboard = ({
	setInstanceName,
	instanceId,
	hasUnsavedChanges,
	setHasUnsavedChanges,
	settings,
	setSettings,
	initialSettings,
	setInitialSettings,
	handleSaveSettings
}) => {
	const [instances, setInstances] = useState({});
	const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);

	// Estados de abertura das abas - agora fora do objeto settings
	const [showQuickSettings, setShowQuickSettings] = useState(() => {
		return localStorage.getItem("showQuickSettings") === "true";
	});
	const [showTimingSettings, setShowTimingSettings] = useState(() => {
		return localStorage.getItem("showTimingSettings") === "true";
	});
	const [showNumberFilters, setShowNumberFilters] = useState(() => {
		return localStorage.getItem("showNumberFilters") === "true";
	});
	const [showFollowUpSettings, setShowFollowUpSettings] = useState(() => {
		return localStorage.getItem("showFollowUpSettings") === "true";
	});
	const [showTTSSettings, setShowTTSSettings] = useState(() => {
		return localStorage.getItem("showTTSSettings") === "true";
	});
	const [showAIMediaSettings, setShowAIMediaSettings] = useState(() => {
		return localStorage.getItem("showAIMediaSettings") === "true";
	});
	const [showAPIFunctionsSettings, setShowAPIFunctionsSettings] = useState(() => {
		return localStorage.getItem("showAPIFunctionsSettings") === "true";
	});
	const [showAlertSettings, setShowAlertSettings] = useState(() => {
		return localStorage.getItem("showAlertSettings") === "true";
	});

	// Estado settings removido - agora é passado como prop do AppLayout

	const [newSomenteResponder, setNewSomenteResponder] = useState("");
	const [newNaoResponder, setNewNaoResponder] = useState("");
	const [existingInstances, setExistingInstances] = useState([]);
	const [exportingNumbers, setExportingNumbers] = useState(false);
	const [importingNumbers, setImportingNumbers] = useState(false);
	const [importNumbersDialogOpen, setImportNumbersDialogOpen] = useState(false);
	const [importNumberType, setImportNumberType] = useState("");
	const [instanceCopyDialogOpen, setInstanceCopyDialogOpen] = useState(false);
	const [selectedInstanceToCopy, setSelectedInstanceToCopy] = useState("");
	// Estados initialSettings e hasUnsavedChanges removidos - agora são passados como props do AppLayout
	const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
	const [geminiPromptModalOpen, setGeminiPromptModalOpen] = useState(false);
	const [gptPromptModalOpen, setGptPromptModalOpen] = useState(false);
	const [purerouterPromptModalOpen, setPurerouterPromptModalOpen] = useState(false);
	const [claudePromptModalOpen, setClaudePromptModalOpen] = useState(false);
	const [showMessageTimerModal, setShowMessageTimerModal] = useState(false);
	const [showFollowUpPromptModal, setShowFollowUpPromptModal] = useState(false);
	const [showSmartAudioModal, setShowSmartAudioModal] = useState(false);
	const [showElevenLabsModal, setShowElevenLabsModal] = useState(false);
	const [useCustomVoice, setUseCustomVoice] = useState(false);
	const [selectedVoice, setSelectedVoice] = useState(settings.ELEVENLABS_VOICE_ID || "pNInz6obpgDQGcFmaJgB");
	const [customVoiceId, setCustomVoiceId] = useState("");

	// Lista de vozes padrão da ElevenLabs para detectar vozes personalizadas
	const ELEVENLABS_DEFAULT_VOICES = [
		"pNInz6obpgDQGcFmaJgB", // Adam
		"ErXwobaYiN019PkySvjV", // Antoni
		"EXAVITQu4vr4xnSDxMaL", // Bella
		"MF3mGyEYCl7XYWbV9V6O", // Elli
		"TxGEqnHWrfWFTfGW9XjX", // Josh
		"AZnzlk1XvdvUeBnXmlld", // Rachel
		"29vD33N1CtxCmqQRPOHJ", // Drew
		"2EiwWnXFnvU5JabPnv8n", // Clyde
		"CYw3kZ02Hs0563khs1Fj"  // Dave
	];

	// Função para verificar se a voz é personalizada
	const isCustomVoice = (voiceId) => {
		if (!voiceId || typeof voiceId !== 'string') return false;
		return !ELEVENLABS_DEFAULT_VOICES.includes(voiceId);
	};

	// Sincronizar estados da modal com as configurações quando ela é aberta
	useEffect(() => {
		if (showElevenLabsModal) {
			const currentVoiceId = settings.ELEVENLABS_VOICE_ID || "pNInz6obpgDQGcFmaJgB";
			const isCustom = isCustomVoice(currentVoiceId);

			console.log(`[ElevenLabs Modal] Sincronizando estados:`, {
				currentVoiceId,
				isCustom,
				previousUseCustomVoice: useCustomVoice,
				previousCustomVoiceId: customVoiceId
			});

			setUseCustomVoice(isCustom);

			if (isCustom) {
				setCustomVoiceId(currentVoiceId);
				setSelectedVoice("pNInz6obpgDQGcFmaJgB"); // Reset para voz padrão
			} else {
				setCustomVoiceId("");
				setSelectedVoice(currentVoiceId);
			}
		}
	}, [showElevenLabsModal, settings.ELEVENLABS_VOICE_ID]);

	const knowledgeBaseActive = !!(
		(settings.GEMINI_KNOWLEDGE_BASE_ENABLED && settings.GEMINI_ACTIVE_STORE_NAME) ||
		settings.GEMINI_WEB_SEARCH_ENABLED
	);

	const sanitizePhoneNumber = (phoneNumber) => {
		return phoneNumber.replace(/\D/g, '');
	};

	// Local state for alert configuration UI
	const [newAlertNumber, setNewAlertNumber] = useState("");
	const [showAlertNumberInput, setShowAlertNumberInput] = useState(false);

	// Converte ALERT_NUMBER de string para array para exibição
	const alertNumbersArray = settings.ALERT_NUMBER 
		? settings.ALERT_NUMBER.split(',').map(n => n.trim()).filter(n => n)
		: [];

	const handleSaveGeminiSettings = ({
		prompt,
		geminiKey,
		geminiModel,
		temperature,
		webSearchEnabled,
		knowledgeBaseEnabled,
		thinkingLevel,
		mediaResolution,
	}) => {
		if (webSearchEnabled && knowledgeBaseEnabled) {
			toast.error(
				'⚠️ "Pesquisa na Web" e "Base de Conhecimento" não podem estar ativos simultaneamente. Esta é uma limitação da API Gemini. Por favor, escolha apenas um.',
				{
					position: "top-center",
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					className: "!bg-statusErrorDark !text-textOnDarkStrong !border !border-statusError"
				}
			);
			return;
		}
		const ensuredTemperature = Math.max(0.01, Number.parseFloat(temperature) || 0.01);
		setSettings((prev) => ({
			...prev,
			GEMINI_PROMPT: prompt,
			GEMINI_KEY: geminiKey,
			GEMINI_MODEL: geminiModel,
			GEMINI_TEMPERATURE: ensuredTemperature,
			GEMINI_WEB_SEARCH_ENABLED: webSearchEnabled,
			GEMINI_KNOWLEDGE_BASE_ENABLED: knowledgeBaseEnabled,
			GEMINI_THINKING_LEVEL: thinkingLevel || "high",
			GEMINI_MEDIA_RESOLUTION: mediaResolution || "medium",
		}));
		setGeminiPromptModalOpen(false);
	};

	const handleSaveGPTSettings = ({
		openaiKey,
		temperature,
		topP,
		openaiModel,
		openaiPrompt,
		reasoningEffort,
		verbosity,
		webSearchEnabled,
		knowledgeBaseEnabled,
	}) => {
		const ensuredTemperature = Math.max(0.01, Number.parseFloat(temperature) || 0.01);
		setSettings((prev) => ({
			...prev,
			OPENAI_KEY: openaiKey,
			OPENAI_TEMPERATURE: ensuredTemperature,
			OPENAI_TOP_P: topP,
			OPENAI_MODEL: openaiModel,
			OPENAI_PROMPT: openaiPrompt,
			OPENAI_REASONING_EFFORT: reasoningEffort,
			OPENAI_VERBOSITY: verbosity,
			OPENAI_WEB_SEARCH_ENABLED: webSearchEnabled,
			OPENAI_KNOWLEDGE_BASE_ENABLED: knowledgeBaseEnabled,
		}));
		setGptPromptModalOpen(false);
	};

	const handleSavePureRouterSettings = ({ prompt }) => {
		setSettings((prev) => ({
			...prev,
			PUREROUTER_PROMPT: prompt,
		}));
		setPurerouterPromptModalOpen(false);
	};

	const handleSaveClaudeSettings = ({
		anthropicKey,
		anthropicModel,
		anthropicPrompt,
		temperature,
		audioProvider,
		audioApiKey,
	}) => {
		const ensuredTemperature = Math.max(0.01, Number.parseFloat(temperature) || 0.01);
		setSettings((prev) => ({
			...prev,
			ANTHROPIC_KEY: anthropicKey,
			ANTHROPIC_MODEL: anthropicModel,
			ANTHROPIC_PROMPT: anthropicPrompt,
			ANTHROPIC_TEMPERATURE: ensuredTemperature,
			ANTHROPIC_AUDIO_PROVIDER: audioProvider,
			ANTHROPIC_AUDIO_API_KEY: audioApiKey,
		}));
		setClaudePromptModalOpen(false);
	};

	const handleSettingsChange = (e) => {
		const { name, value, type, checked } = e.target;
		const newValue = type === 'checkbox' ? checked : value;

		console.log('🔍 [DASHBOARD HANDLE DEBUG] Recebendo mudança:', {
			name,
			value,
			type,
			checked,
			newValue,
			isCalendarToggle: name === 'GOOGLE_CALENDAR_CUSTOM_MESSAGE_ENABLED'
		});

		// Verifica conflito entre TTS_SMART_AUDIO e ENVIO_EM_BLOCO
		if (name === 'TTS_SMART_AUDIO' && newValue === true && settings.ENVIO_EM_BLOCO) {
			toast.error(
				'⚠️ Para habilitar o "Áudio Inteligente", é necessário desativar a opção "Enviar resposta em uma mensagem"',
				{
					position: "top-center",
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					className: "!bg-statusErrorDark !text-textOnDarkStrong !border !border-statusError"
				}
			);
			return; // Não aplica a mudança
		}

		// Verifica conflito reverso - ENVIO_EM_BLOCO quando TTS_SMART_AUDIO está ativo
		if (name === 'ENVIO_EM_BLOCO' && newValue === true && settings.TTS_SMART_AUDIO) {
			toast.error(
				'⚠️ Para habilitar "Enviar resposta em uma mensagem", é necessário desativar a opção "Áudio Inteligente"',
				{
					position: "top-center",
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					className: "!bg-statusErrorDark !text-textOnDarkStrong !border !border-statusError"
				}
			);
			return; // Não aplica a mudança
		}

		// Se está desativando o follow-up, cancela todos os follow-ups ativos
		if (name === 'FOLLOW_UP_ATIVO' && !newValue && settings.FOLLOW_UP_ATIVO) {
			console.log('🚫 Follow-up sendo desativado. Cancelando todos os follow-ups ativos...');

			// Cancela todos os follow-ups da instância
			ipcRenderer.invoke('cancel-all-follow-ups', { instanceId })
				.then((result) => {
					if (result.success) {
						console.log(`✅ ${result.cancelledCount} follow-ups cancelados com sucesso`);
						toast.success(`Follow-up desativado. ${result.cancelledCount} mensagens agendadas foram canceladas.`);
					} else {
						console.error('❌ Erro ao cancelar follow-ups:', result.message);
						toast.error('Erro ao cancelar follow-ups: ' + result.message);
					}
				})
				.catch((error) => {
					console.error('❌ Erro ao cancelar follow-ups:', error);
					toast.error('Erro ao cancelar follow-ups');
				});
		}

		setSettings((prevSettings) => {
			const newSettings = {
				...prevSettings,
				[name]: newValue,
			};

			console.log('🔍 [DASHBOARD STATE DEBUG] Aplicando mudança no estado:', {
				name,
				newValue,
				prevValue: prevSettings[name],
				isCalendarToggle: name === 'GOOGLE_CALENDAR_CUSTOM_MESSAGE_ENABLED',
				newCalendarValue: newSettings.GOOGLE_CALENDAR_CUSTOM_MESSAGE_ENABLED
			});

			return newSettings;
		});
	};

	// Função handleSaveSettings removida - agora é passada como prop do AppLayout

	const handleCopySettings = () => {
		if (selectedInstanceToCopy) {
			ipcRenderer.removeAllListeners(channels.COPY_SETTINGS_REPLY);

			ipcRenderer.once(channels.COPY_SETTINGS_REPLY, (_, response) => {
				const isWrapped =
					response && typeof response === "object" && "settings" in response;
				const loadedSettings = isWrapped ? response.settings : response;
				const copiedExtras = isWrapped ? response.copiedExtras : null;

				if (loadedSettings) {
					setSettings(loadedSettings);
					setInitialSettings(loadedSettings);

					window.dispatchEvent(
						new CustomEvent("instance-data-imported", {
							detail: { copiedExtras },
						}),
					);

					const extras = [];
					if (copiedExtras?.functions) extras.push("ferramentas de IA");
					if (copiedExtras?.aiMedia) extras.push("mídias da IA");

					if (extras.length > 0) {
						toast.success(
							`Configurações copiadas com sucesso! (${extras.join(" e ")} também copiadas)`,
						);
					} else {
						toast.success("Configurações copiadas com sucesso!");
					}

					if (copiedExtras?.errors?.length > 0) {
						toast.warn(
							`Alguns itens não foram copiados: ${copiedExtras.errors.join("; ")}`,
						);
					}

					toast.info("Não esqueça de salvar as configurações para mantê-las.");
				} else {
					toast.error(
						"Erro ao copiar configurações. Dados recebidos inválidos.",
					);
				}
			});

			ipcRenderer.send(
				channels.COPY_SETTINGS,
				selectedInstanceToCopy,
				instanceId,
			);
			setInstanceCopyDialogOpen(false);
		} else {
			toast.warn("Por favor, selecione uma instância para copiar.");
		}
	};

	const handleExportNumbers = async (numberType) => {
		setExportingNumbers(true);
		try {
			const numbersToExport = numberType === 'SOMENTE_RESPONDER'
				? settings.SOMENTE_RESPONDER || []
				: settings.NAO_RESPONDER || [];

			const result = await ipcRenderer.invoke('export-numbers', { numbers: numbersToExport });

			if (result.success) {
				toast.success(`Números exportados com sucesso! ${result.exportedCount} números salvos.`);
			} else {
				toast.error(result.error || 'Erro ao exportar números');
			}
		} catch (error) {
			toast.error('Erro ao exportar números');
		} finally {
			setExportingNumbers(false);
		}
	};

	const handleImportNumbers = (numberType) => {
		setImportNumberType(numberType);
		setImportNumbersDialogOpen(true);
	};

	const handleImportNumbersFromDialog = async (numbersToImport, numberType) => {
		setImportingNumbers(true);
		try {
			const currentNumbers = numberType === 'SOMENTE_RESPONDER'
				? settings.SOMENTE_RESPONDER || []
				: settings.NAO_RESPONDER || [];

			const uniqueNumbers = [...new Set([...currentNumbers, ...numbersToImport])];

			setSettings((prevSettings) => ({
				...prevSettings,
				[numberType]: uniqueNumbers,
			}));

			toast.success(`${numbersToImport.length} números importados com sucesso!`);
		} catch (error) {
			toast.error('Erro ao importar números');
		} finally {
			setImportingNumbers(false);
		}
	};

		// Função para formatar o número de telefone para exibição
	const formatPhoneNumberForDisplay = (phoneNumber) => {
		if (!phoneNumber) return '';

		// Remove caracteres não numéricos
		const cleanNumber = phoneNumber.replace(/\D/g, '');

		// Formata número brasileiro de celular (13 dígitos: 55 + DDD + 9 + 8 dígitos)
		if (cleanNumber.length === 13 && cleanNumber.startsWith('55')) {
			// Formato: +55 (XX) 9XXXX-XXXX
			const countryCode = cleanNumber.substring(0, 2);
			const areaCode = cleanNumber.substring(2, 4);
			const nineDigit = cleanNumber.substring(4, 5);
			const firstPart = cleanNumber.substring(5, 9);
			const secondPart = cleanNumber.substring(9, 13);
			return `+${countryCode} (${areaCode}) ${nineDigit}${firstPart}-${secondPart}`;
		}

		// Formata número brasileiro de celular truncado (12 dígitos: 55 + DDD + 9 + 7 dígitos)
		// Este caso cobre números como 555192194386 onde o Baileys trunca o último dígito
		if (cleanNumber.length === 12 && cleanNumber.startsWith('55') && cleanNumber.substring(4, 5) === '9') {
			// Formato: +55 (XX) XXXX-XXXX
			const countryCode = cleanNumber.substring(0, 2);
			const areaCode = cleanNumber.substring(2, 4);
			const firstPart = cleanNumber.substring(4, 8);  // 9219 (inclui o 9 + próximos 3 dígitos)
			const secondPart = cleanNumber.substring(8, 12); // 4386 (últimos 4 dígitos disponíveis)
			return `+${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
		}

		// Formata número brasileiro fixo (12 dígitos: 55 + DDD + 8 dígitos)
		if (cleanNumber.length === 12 && cleanNumber.startsWith('55')) {
			// Formato: +55 (XX) XXXX-XXXX
			const countryCode = cleanNumber.substring(0, 2);
			const areaCode = cleanNumber.substring(2, 4);
			const firstPart = cleanNumber.substring(4, 8);
			const secondPart = cleanNumber.substring(8, 12);
			return `+${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
		}

		// Formata números de outros países com 10-15 dígitos
		if (cleanNumber.length >= 10 && cleanNumber.length <= 15) {
			// Para números de 13 dígitos que não são brasileiros
			if (cleanNumber.length === 13 && !cleanNumber.startsWith('55')) {
				const countryCode = cleanNumber.substring(0, 2);
				const remaining = cleanNumber.substring(2);
				return `+${countryCode} ${remaining}`;
			}

			// Para números de 12 dígitos que não são brasileiros
			if (cleanNumber.length === 12 && !cleanNumber.startsWith('55')) {
				const countryCode = cleanNumber.substring(0, 2);
				const remaining = cleanNumber.substring(2);
				return `+${countryCode} ${remaining}`;
			}
		}

		// Formata número brasileiro não normalizado (13 dígitos: 55 + DDD + 9 dígitos)
		if (cleanNumber.length === 13 && cleanNumber.startsWith('55')) {
			// Formato: +55 (XX) XXXXX-XXXX
			const ddd = cleanNumber.substring(2, 4);
			const firstPart = cleanNumber.substring(4, 9);
			const secondPart = cleanNumber.substring(9, 13);
			return `+55 (${ddd}) ${firstPart}-${secondPart}`;
		}

		// Para outros formatos, apenas adiciona espaços
		if (cleanNumber.length > 10) {
			return `+${cleanNumber}`;
		}

		return phoneNumber;
	};

	// Função para verificar o status atual da conexão
	const checkConnectionStatus = async () => {
		try {
			console.log(
				`[UI] Verificando status de conexão para instância: ${instanceId}`,
			);

			const status = await ipcRenderer.invoke(
				"get-connection-status",
				instanceId,
			);
			console.log(`[UI] Status recebido: ${JSON.stringify(status)}`);

			setInstances((prevInstances) => ({
				...prevInstances,
				[instanceId]: {
					...prevInstances[instanceId],
					connected: status.connected,
					phoneNumber: status.phoneNumber,
				},
			}));
			return status;
		} catch (error) {
			console.error("[UI] Erro ao obter status de conexão:", error);
			// Em caso de erro, assumimos desconectado
			const errorStatus = { connected: false };
			setInstances((prevInstances) => ({
				...prevInstances,
				[instanceId]: {
					...prevInstances[instanceId],
					connected: false,
				},
			}));
			return errorStatus;
		}
	};

	useEffect(() => {
		ipcRenderer.send(channels.SET_INSTANCE_ID, instanceId);
		if (setInstanceName) {
			setInstanceName(instanceId === "default" ? "Padrão" : instanceId);
		}

		setInstances((prevInstances) => ({
			...prevInstances,
			[instanceId]: {
				...prevInstances[instanceId],
				connected: false,
			},
		}));

		checkConnectionStatus();

		const initialCheckTimeout = setTimeout(() => {
			checkConnectionStatus();
		}, 1000);

		const statusCheckInterval = setInterval(checkConnectionStatus, 10000);

		return () => {
			clearInterval(statusCheckInterval);
			clearTimeout(initialCheckTimeout);
		};
	}, [instanceId, setInstanceName]);

	useEffect(() => {
		const TOAST_ID = "unsaved-changes";
		const hasChanges =
			JSON.stringify(settings) !== JSON.stringify(initialSettings);

		console.log({ settings, initialSettings });

		setHasUnsavedChanges(hasChanges);

		if (hasChanges && !toast.isActive(TOAST_ID)) {
			toast.warning("Alterações não salvas. Lembre-se de salvar!", {
				position: "top-right",
				autoClose: false,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				toastId: TOAST_ID,
			});
		} else if (!hasChanges && toast.isActive(TOAST_ID)) {
			toast.dismiss(TOAST_ID);
		}
	}, [settings, initialSettings]);



	useEffect(() => {
		const isTemporaryPassword = localStorage.getItem("isTemporaryPassword");

		if (isTemporaryPassword) {
			setShowChangePasswordModal(true);
		}
	}, []);

	const showCopyKeyButton =
		((!settings.OPENAI_KEY && settings.AI_SELECTED === "GPT") ||
			(!settings.GEMINI_KEY && settings.AI_SELECTED === "GEMINI") ||
			(!settings.ANTHROPIC_KEY && settings.AI_SELECTED === "CLAUDE")) &&
		instanceId !== "default";

	// Atualizando os manipuladores de abertura/fechamento das abas para usar o localStorage
	const toggleQuickSettings = () => {
		const newValue = !showQuickSettings;
		setShowQuickSettings(newValue);
		localStorage.setItem("showQuickSettings", newValue.toString());
	};

	const toggleTimingSettings = () => {
		const newValue = !showTimingSettings;
		setShowTimingSettings(newValue);
		localStorage.setItem("showTimingSettings", newValue.toString());
	};

	const toggleNumberFilters = () => {
		const newValue = !showNumberFilters;
		setShowNumberFilters(newValue);
		localStorage.setItem("showNumberFilters", newValue.toString());
	};

	const toggleFollowUpSettings = () => {
		const newState = !showFollowUpSettings;
		setShowFollowUpSettings(newState);
		localStorage.setItem("showFollowUpSettings", newState.toString());
	};

	const toggleTTSSettings = () => {
		const newState = !showTTSSettings;
		setShowTTSSettings(newState);
		localStorage.setItem("showTTSSettings", newState.toString());
	};

	const toggleAIMediaSettings = () => {
		const newState = !showAIMediaSettings;
		setShowAIMediaSettings(newState);
		localStorage.setItem("showAIMediaSettings", newState.toString());
	};

	const toggleAPIFunctionsSettings = () => {
		const newState = !showAPIFunctionsSettings;
		setShowAPIFunctionsSettings(newState);
		localStorage.setItem("showAPIFunctionsSettings", newState.toString());
	};

	const toggleAlertSettings = () => {
		const newState = !showAlertSettings;
		setShowAlertSettings(newState);
		localStorage.setItem("showAlertSettings", newState.toString());
	};

	// Carregar configurações e instâncias
	useEffect(() => {
		ipcRenderer.send(channels.LOAD_SETTINGS, instanceId);

		ipcRenderer.on(channels.LOAD_SETTINGS_REPLY, (_, loadedSettings) => {
			if (loadedSettings) {
				// Garante que a nova configuração tenha o valor padrão se não existir
				const settingsWithDefaults = {
					...loadedSettings,
					INTERVENCAO_HUMANA_ATIVA: loadedSettings.INTERVENCAO_HUMANA_ATIVA !== undefined
						? loadedSettings.INTERVENCAO_HUMANA_ATIVA
						: true
				};
				setSettings(settingsWithDefaults);
				setInitialSettings(settingsWithDefaults);
			}
		});

		ipcRenderer.send(channels.GET_INSTANCE_LIST);

		ipcRenderer.on(channels.GET_INSTANCE_LIST_REPLY, (_, instances) => {
			setExistingInstances(instances);
		});

		ipcRenderer.on(channels.log, (_, arg) => {
			console.log(arg);
		});

		ipcRenderer.on(channels.SAVE_SETTINGS_REPLY, (_, response) => {
			if (response.success) {
				toast.success("Configurações salvas com sucesso!");
			} else {
				toast.error("Erro ao salvar configurações!");
			}
		});

		ipcRenderer.on(channels.START_WHATS, (_, arg) => {
			const { qrCodeBase64, instanceId: incomingInstanceId } = arg;
			setInstances((prevInstances) => ({
				...prevInstances,
				[incomingInstanceId]: {
					...prevInstances[incomingInstanceId],
					qrCode: qrCodeBase64,
				},
			}));
		});

		ipcRenderer.on(channels.PAIRING_CODE, (_, arg) => {
			const { pairingCode, instanceId: incomingInstanceId } = arg;
			setInstances((prevInstances) => ({
				...prevInstances,
				[incomingInstanceId]: {
					...prevInstances[incomingInstanceId],
					pairingCode,
					pairingError: null,
				},
			}));
		});

		ipcRenderer.on(channels.PAIRING_CODE_ERROR, (_, arg) => {
			const { message, instanceId: incomingInstanceId } = arg;
			setInstances((prevInstances) => ({
				...prevInstances,
				[incomingInstanceId]: {
					...prevInstances[incomingInstanceId],
					pairingCode: null,
					pairingError: message,
				},
			}));
			toast.error(`Erro ao gerar código de pareamento: ${message}`);
		});

		ipcRenderer.on(channels.STATUS_UPDATE, (_, arg) => {
			const { status, instanceId: incomingInstanceId, reason } = arg;
			const isConnected = status === "open";
			const isCloseByUser = status === "close-by-user";
			const isDisconnectedByValidation =
				status === "disconnected-by-validation";
			const isDisconnectedByError = status === "disconnected-by-error";

			setInstances((prevInstances) => {
				const updatedInstance = {
					...prevInstances[incomingInstanceId],
					connected: isConnected,
					connectionReason:
						reason || prevInstances[incomingInstanceId]?.connectionReason,
				};

				if (isConnected) {
					updatedInstance.qrCode = null;
					updatedInstance.pairingCode = null;
					updatedInstance.pairingError = null;
					setQrCodeDialogOpen(false);
					toast.success("WhatsApp conectado com sucesso!");
					// Atualiza o número do telefone quando conecta
					checkConnectionStatus();
				}

				if (isCloseByUser) {
					updatedInstance.qrCode = null;
					updatedInstance.pairingCode = null;
					updatedInstance.pairingError = null;
					setQrCodeDialogOpen(false);
					toast.warning(
						"Conexão encerrada pelo usuário. Escaneie o QR Code novamente.",
					);
				}

				if (isDisconnectedByValidation || isDisconnectedByError) {
					updatedInstance.qrCode = null;
					updatedInstance.pairingCode = null;
					updatedInstance.pairingError = null;
					updatedInstance.connected = false;
					toast.error(
						`WhatsApp desconectado: ${reason || "Erro de validação"}`,
						{
							autoClose: false,
						},
					);
				}

				return {
					...prevInstances,
					[incomingInstanceId]: updatedInstance,
				};
			});
		});

		return () => {
			// Remover apenas os listeners específicos do Dashboard
			ipcRenderer.removeAllListeners(channels.LOAD_SETTINGS_REPLY);
			ipcRenderer.removeAllListeners(channels.GET_INSTANCE_LIST_REPLY);
			ipcRenderer.removeAllListeners(channels.log);
			ipcRenderer.removeAllListeners(channels.SAVE_SETTINGS_REPLY);
			ipcRenderer.removeAllListeners(channels.START_WHATS);
			ipcRenderer.removeAllListeners(channels.PAIRING_CODE);
			ipcRenderer.removeAllListeners(channels.PAIRING_CODE_ERROR);
			ipcRenderer.removeAllListeners(channels.STATUS_UPDATE);
		};
	}, [instanceId, setQrCodeDialogOpen]);

	return (
		<div className="relative flex min-h-screen flex-col items-center bg-slateBase py-8 text-textPrimary overflow-hidden">
			<div className="fixed inset-0 pointer-events-none overflow-hidden">
				<div className="absolute top-20 left-1/2 -translate-x-1/2 w-96 h-96 bg-gradient-radial from-cyanTertiary/30 via-tealSecondary/15 to-transparent rounded-full blur-xl"></div>
				<div className="absolute top-1/3 -left-20 w-80 h-80 bg-gradient-radial from-emeraldPrimary/25 via-cyanTertiary/10 to-transparent rounded-full blur-xl"></div>
				<div className="absolute top-2/3 -right-20 w-72 h-72 bg-gradient-radial from-tealPrimary/20 via-cyanSecondary/8 to-transparent rounded-full blur-xl"></div>
				<div className="absolute inset-0 bg-gradient-to-br from-cyanDeep/5 via-transparent to-slateBase/8"></div>
			</div>

			<div className="relative z-10 w-full flex flex-col items-center">
				<QRCodeDialog
					isOpen={qrCodeDialogOpen}
					onClose={(open) => {
						setQrCodeDialogOpen(open);
						if (!open) {
							const wasPairing = Boolean(
								instances[instanceId]?.pairingCode ||
									instances[instanceId]?.pairingError,
							);
							setInstances((prevInstances) => ({
								...prevInstances,
								[instanceId]: {
									...prevInstances[instanceId],
									pairingCode: null,
									pairingError: null,
								},
							}));
							if (wasPairing) {
								ipcRenderer
									.invoke(channels.DISCONNECT_INSTANCE, instanceId)
									.catch((error) => {
										console.error(
											"Erro ao cancelar pareamento ao fechar dialog:",
											error,
										);
									});
							}
						}
					}}
					qrCode={instances[instanceId]?.qrCode}
					pairingCode={instances[instanceId]?.pairingCode}
					pairingError={instances[instanceId]?.pairingError}
					instanceId={instanceId}
				/>
				<ChangePasswordModal
					isOpen={showChangePasswordModal}
					onClose={() => {
						setShowChangePasswordModal(false);
						localStorage.removeItem("isTemporaryPassword");
					}}
				/>

				<OzapAISettingsDialog
					isOpen={purerouterPromptModalOpen}
					onClose={() => setPurerouterPromptModalOpen(false)}
					prompt={settings.PUREROUTER_PROMPT}
					onSave={handleSavePureRouterSettings}
					instanceId={instanceId}
				/>

				<GeminiSettingsDialog
					isOpen={geminiPromptModalOpen}
					onClose={() => {
						setGeminiPromptModalOpen(false);
					}}
					geminiPrompt={settings.GEMINI_PROMPT}
					geminiKey={settings.GEMINI_KEY}
					geminiModel={settings.GEMINI_MODEL}
					temperature={Math.max(0.01, settings.GEMINI_TEMPERATURE || 0.7)}
					webSearchEnabled={settings.GEMINI_WEB_SEARCH_ENABLED || false}
					knowledgeBaseEnabled={settings.GEMINI_KNOWLEDGE_BASE_ENABLED || false}
					thinkingLevel={settings.GEMINI_THINKING_LEVEL || "high"}
					mediaResolution={settings.GEMINI_MEDIA_RESOLUTION || "medium"}
					onSave={handleSaveGeminiSettings}
					instanceId={instanceId}
				/>
				<GPTSettingsDialog
					isOpen={gptPromptModalOpen}
					onClose={() => setGptPromptModalOpen(false)}
					onSave={handleSaveGPTSettings}
					openaiKey={settings.OPENAI_KEY}
					temperature={Math.max(0.01, settings.OPENAI_TEMPERATURE || 0.7)}
					topP={settings.OPENAI_TOP_P}
					openaiModel={settings.OPENAI_MODEL}
					openaiPrompt={settings.OPENAI_PROMPT}
					reasoningEffort={settings.OPENAI_REASONING_EFFORT}
					verbosity={settings.OPENAI_VERBOSITY}
					webSearchEnabled={settings.OPENAI_WEB_SEARCH_ENABLED || false}
					knowledgeBaseEnabled={settings.OPENAI_KNOWLEDGE_BASE_ENABLED || false}
					instanceId={instanceId}
				/>
				<ClaudeSettingsDialog
					isOpen={claudePromptModalOpen}
					onClose={() => setClaudePromptModalOpen(false)}
					anthropicKey={settings.ANTHROPIC_KEY}
					anthropicModel={settings.ANTHROPIC_MODEL}
					anthropicPrompt={settings.ANTHROPIC_PROMPT}
					temperature={Math.max(0.01, settings.ANTHROPIC_TEMPERATURE || 0.7)}
					audioProvider={settings.ANTHROPIC_AUDIO_PROVIDER || "OPENAI"}
					audioApiKey={settings.ANTHROPIC_AUDIO_API_KEY || ""}
					existingOpenaiKey={settings.OPENAI_KEY || ""}
					existingGeminiKey={settings.GEMINI_KEY || ""}
					onSave={handleSaveClaudeSettings}
				/>
				<Dialog
					open={instanceCopyDialogOpen}
					onOpenChange={setInstanceCopyDialogOpen}
				>
					<DialogContent className="max-w-2xl border-2 border-borderPrimary/60 bg-slateCard">
					<DialogHeader className="border-b border-borderPrimary/20 pb-4">
							<DialogTitle className="text-textPrimary">
								Selecionar Instância para Copiar Configurações
							</DialogTitle>
							<p className="text-sm text-textSecondary">
								Escolha uma instância existente para copiar todas as suas
								configurações.
							</p>
						</DialogHeader>
						<div className="mt-4 space-y-4">
							<select
								className="w-full rounded-lg border border-borderPrimary/30 bg-grayBase/80 px-4 py-3 text-textPrimary focus:border-borderPrimary focus:ring focus:ring-borderPrimary/30"
								value={selectedInstanceToCopy}
								onChange={(e) => setSelectedInstanceToCopy(e.target.value)}
							>
								<option value="">Escolha uma instância</option>
								{existingInstances
									.filter((instance) => instance !== instanceId)
									.map((instance) => (
										<option key={instance} value={instance}>
											{instance === "default" ? "Padrão" : instance}
										</option>
									))}
							</select>
							<div className="flex justify-end space-x-2">
								<button
									onClick={handleCopySettings}
									type="button"
									className="rounded-lg bg-gradient-to-r from-gradientPrimary to-limePrimary px-4 py-2 font-medium text-slateBase transition hover:shadow-lg hover:shadow-gradientPrimary/25 hover:from-limePrimary hover:to-gradientPrimary"
								>
									Copiar Configurações
								</button>
							</div>
						</div>
					</DialogContent>
				</Dialog>

				<MessageTimerDialog
					open={showMessageTimerModal}
					onClose={() => setShowMessageTimerModal(false)}
					settings={settings}
					setSettings={setSettings}
				/>

				<FollowUpPromptDialog
					isOpen={showFollowUpPromptModal}
					onClose={() => setShowFollowUpPromptModal(false)}
					currentPrompt={settings.FOLLOW_UP_PROMPT}
					onSave={(newPrompt) => {
						setSettings((prev) => ({
							...prev,
							FOLLOW_UP_PROMPT: newPrompt,
						}));
					}}
				/>

				<SmartAudioDialog
					isOpen={showSmartAudioModal}
					onClose={setShowSmartAudioModal}
					settings={settings}
					onSettingsChange={handleSettingsChange}
				/>

				<ElevenLabsDialog
					isOpen={showElevenLabsModal}
					onClose={setShowElevenLabsModal}
					settings={settings}
					onSettingsChange={handleSettingsChange}
					onSave={() => {
						const voiceIdToSave = useCustomVoice ? customVoiceId : selectedVoice;

						console.log(`[ElevenLabs Modal] Salvando configurações:`, {
							useCustomVoice,
							selectedVoice,
							customVoiceId,
							voiceIdToSave,
							previousVoiceId: settings.ELEVENLABS_VOICE_ID,
							isCustomDetected: isCustomVoice(voiceIdToSave)
						});

						// Validação antes de salvar
						if (useCustomVoice && (!customVoiceId || customVoiceId.trim() === "")) {
							console.error(`[ElevenLabs Modal] Erro: Voz personalizada selecionada mas ID está vazio`);
							return;
						}

						setSettings(prev => ({
							...prev,
							ELEVENLABS_VOICE_ID: voiceIdToSave
						}));

						console.log(`[ElevenLabs Modal] Configuração salva com sucesso - Voice ID: ${voiceIdToSave}`);
						handleSaveSettings();
						setShowElevenLabsModal(false);
					}}
					useCustomVoice={useCustomVoice}
					setUseCustomVoice={setUseCustomVoice}
					selectedVoice={selectedVoice}
					setSelectedVoice={setSelectedVoice}
					customVoiceId={customVoiceId}
					setCustomVoiceId={setCustomVoiceId}
				/>

				<ImportNumbersDialog
					isOpen={importNumbersDialogOpen}
					onClose={() => setImportNumbersDialogOpen(false)}
					onImport={handleImportNumbersFromDialog}
					numberType={importNumberType}
					isImporting={importingNumbers}
				/>

				<div className="mb-8 w-full max-w-md mx-auto relative">
					<div className="relative bg-whiteBase/10 backdrop-blur-md border border-whiteBase/20 rounded-2xl shadow-lg shadow-emeraldBase/10 overflow-hidden">
						<div className="relative z-10 p-6 space-y-6">
							{/* Header do status */}
							<div className="flex items-center justify-between">
								<div className="flex items-center space-x-3">
									<div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emeraldBase/30 to-cyanPrimary/30 shadow-lg border border-emeraldMedium/40">
										<Smartphone className="w-6 h-6 text-emeraldLight" />
									</div>
									<div>
										<h2 className="text-lg font-semibold text-textPrimary">Status WhatsApp</h2>
										<p className="text-sm text-textSecondary">
											{instances[instanceId]?.connected === true ? 'Bot ativo e funcionando' : 'Conecte para ativar o bot'}
										</p>
									</div>
								</div>
								<div className={`px-3 py-1.5 text-xs font-medium rounded-xl border shadow-sm transition-all duration-300 ${instances[instanceId]?.connected === true
									? 'bg-gradient-to-r from-emeraldBase/20 to-cyanPrimary/20 text-emeraldLight border-emeraldMedium/30 shadow-emeraldMedium/20'
									: 'bg-gradient-to-r from-redBase/20 to-redMedium/20 text-statusErrorLight border-redMedium/30 shadow-redMedium/20'
									}`}>
									{instances[instanceId]?.connected === true ? 'ONLINE' : 'OFFLINE'}
								</div>
							</div>

							{/* Status de conexão */}
							{instances[instanceId]?.connected === true ? (
								<div className="space-y-4">
									{/* Indicador conectado */}
									<div className="flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-r from-emeraldBase/20 to-cyanPrimary/20 border border-emeraldMedium/30 shadow-lg shadow-emeraldMedium/10">
										<div className="flex items-center">
											<CheckCircleIcon className="mr-3 h-6 w-6 text-emeraldLight" />
											<span className="text-lg font-medium text-emeraldLight">
												Conectado ao WhatsApp
											</span>
										</div>
										{instances[instanceId]?.phoneNumber && (
											<span className="text-sm text-emeraldLight/80 mt-2 font-medium">
												{formatPhoneNumberForDisplay(instances[instanceId]?.phoneNumber)}
											</span>
										)}
									</div>

									{/* Botões de ação */}
									<div className="grid grid-cols-2 gap-3">
										<div className="relative">
											<button
												className="w-full relative rounded-xl text-textPrimary px-4 py-3 font-medium transition-all duration-300 bg-gradient-to-r from-slateButton to-slateContent hover:from-slateQuaternary hover:to-slateButton shadow-lg hover:shadow-slateQuaternary/25 hover:scale-[1.02] cursor-pointer border border-slateQuaternary/30"
												onClick={async () => {
													try {
														const response = await ipcRenderer.invoke(
															channels.DISCONNECT_INSTANCE,
															instanceId,
														);
														if (response.success) {
															setInstances((prevInstances) => ({
																...prevInstances,
																[instanceId]: {
																	...prevInstances[instanceId],
																	connected: false,
																},
															}));
															toast.info(response.message);
															setTimeout(checkConnectionStatus, 1000);
														} else {
															toast.error(response.message);
														}
													} catch (error) {
														toast.error("Erro ao desconectar. Tente novamente.");
														console.error(error);
													}
												}}
												type="button"
											>
												<span className="relative z-10">Desconectar</span>
											</button>

										</div>

										<div className="relative">
											<button
												className="w-full relative rounded-xl text-textOnDarkStrong px-4 py-3 font-medium transition-all duration-300 bg-gradient-to-r from-redBase to-redDark hover:from-redMedium hover:to-redBase shadow-lg hover:shadow-redMedium/25 hover:scale-[1.02] cursor-pointer"
												onClick={async () => {
													if (
														window.confirm(
															"Tem certeza que deseja apagar a autenticação? Você precisará escanear o QR Code novamente e perderá todas as conversas.",
														)
													) {
														try {
															const response = await ipcRenderer.invoke(
																channels.LOGOUT_INSTANCE,
																instanceId,
															);
															if (response.success) {
																setInstances((prevInstances) => ({
																	...prevInstances,
																	[instanceId]: {
																		...prevInstances[instanceId],
																		connected: false,
																	},
																}));
																toast.warn(response.message);
																setTimeout(checkConnectionStatus, 1000);
															} else {
																toast.error(response.message);
															}
														} catch (error) {
															toast.error(
																"Erro ao realizar logout. Tente novamente.",
															);
															console.error(error);
														}
													}
												}}
												type="button"
											>
												<span className="relative z-10">Logout</span>
											</button>

										</div>
									</div>
								</div>
							) : (
								<div className="space-y-4">
									{/* Indicador desconectado */}
									<div className="flex items-center justify-center p-4 rounded-xl bg-gradient-to-r from-redBase/20 to-redMedium/20 border border-redMedium/30 shadow-lg shadow-redMedium/10">
										<XCircleIcon className="mr-3 h-6 w-6 text-statusErrorLight" />
										<span className="text-lg font-medium text-statusErrorLight">
											Desconectado do WhatsApp
										</span>
									</div>

									{/* Botão conectar */}
									<button
										type="button"
										onClick={async () => {
											try {
												const status = await checkConnectionStatus();
												if (status.connected) {
													toast.info("WhatsApp já está conectado!");
												} else if (status.pairingInProgress) {
													await ipcRenderer.invoke(channels.DISCONNECT_INSTANCE, instanceId);
													setQrCodeDialogOpen(true);
												} else if (status.hasAuth) {
													ipcRenderer.send(channels.START_WHATS, instanceId);
													toast.info("Reconectando ao WhatsApp...");
												} else {
													setQrCodeDialogOpen(true);
												}
											} catch (error) {
												console.error("Erro ao verificar conexão:", error);
												setQrCodeDialogOpen(true);
											}
										}}
										className="group w-full relative rounded-xl text-textOnDarkStrong px-6 py-4 font-semibold text-lg transition-all duration-200 bg-gradient-to-r from-emeraldBase to-cyanPrimary hover:from-emeraldMedium hover:to-cyanTertiary shadow-lg hover:shadow-emeraldMedium/30 hover:scale-[1.02] cursor-pointer"
										style={{ cursor: "pointer", pointerEvents: "auto" }}
										disabled={!instanceId}
									>
										<span className="relative z-10 flex items-center justify-center">
											<Smartphone className="w-5 h-5 mr-3" />
											Conectar WhatsApp
										</span>
									</button>
								</div>
							)}
						</div>
					</div>
				</div>

				<div className="w-full max-w-5xl rounded-xl overflow-hidden border-2 border-borderPrimary bg-slateCard p-6 pb-4 shadow-2xl shadow-borderPrimary/20">
					{showCopyKeyButton && (
						<div className="group relative flex items-center justify-center mb-4">
							<button
								type="button"
								className="rounded-full border border-borderPrimary bg-borderPrimary/20 px-4 py-2 text-sm text-borderPrimary transition hover:bg-borderPrimary hover:text-slateBase hover:shadow-md hover:shadow-borderPrimary/25"
								onClick={() => {
									setInstanceCopyDialogOpen(true);
								}}
							>
								Importar Configurações
							</button>
						</div>
					)}

					<form>
						<div className="mb-8 relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 rounded-xl p-6 border border-emeraldMedium/50 shadow-lg hover:shadow-emeraldBase/25 transition-shadow duration-200">
							<div className="relative z-10 flex justify-between items-center mb-4">
								<div className="flex items-center gap-4">
									<div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emeraldBase/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60">
										<Brain className="w-6 h-6 text-emeraldLight" />
									</div>
									<div>
										<h2 className="text-xl font-semibold text-textOnDarkStrong flex items-center gap-3">
											Modelo IA
											<span className="px-2.5 py-1 text-xs font-medium bg-gradient-to-r from-emeraldBase/20 to-cyanPrimary/20 text-emeraldLight rounded-full border border-emeraldMedium/30 shadow-sm">
												IA
											</span>
										</h2>
										<p className="text-textOnDarkMedium text-sm">Configure o modelo de inteligência artificial</p>
									</div>
								</div>
								<button
									type="button"
									onClick={async () => {
										await ipcRenderer.invoke(channels.RESET_CHATS, instanceId);
										toast.success(
											"Modelo atualizado com sucesso! Teste agora suas modificações.",
										);
										toast.info(
											"As conversas foram reiniciadas. A IA perdeu a memória das interações anteriores.",
										);
									}}
									className="ml-1 bg-transparent rounded-full border border-emeraldMedium/50 p-2 text-emeraldLight hover:bg-emeraldBase/20 transition-all duration-300 hover:border-emeraldMedium hover:text-emeraldLight"
									title="Recarregar instâncias"
								>
									<RefreshCwIcon className="size-5" />
								</button>
							</div>
							<div className="relative z-10 grid grid-cols-2 gap-4">
								<div>
									<div className="mb-1 flex items-center gap-2">
										<label
											htmlFor="ai-selected"
											className="font-medium text-textPrimary"
										>
											IA Selecionada
										</label>

									</div>
									<select
										id="ai-selected"
										name="AI_SELECTED"
										value={settings.AI_SELECTED}
										onChange={handleSettingsChange}
										className="w-full rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-3 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
									>
										<option value="GPT">GPT</option>
										<option value="GEMINI">GEMINI</option>
										<option value="PUREROUTER">oZap AI</option>
										<option value="CLAUDE">Claude</option>
									</select>
								</div>

								{settings.AI_SELECTED === "GEMINI" && (
									<>
										<div>
											<span
												htmlFor="gemini-prompt"
												className="mb-1 block opacity-0 invisible font-medium text-grayLight"
											>
												GEMINI Prompt
											</span>

											<button
												onClick={() => setGeminiPromptModalOpen(true)}
												type="button"
												className="w-full relative rounded-lg text-textOnDarkStrong px-4 py-3 transition-all duration-200 transform hover:scale-[1.01] bg-gradient-to-r from-emeraldBase via-cyanPrimary to-tealPrimary hover:shadow-lg hover:shadow-emeraldBase/25 cursor-pointer"
												style={{ cursor: "pointer", pointerEvents: "auto" }}
											>
												<span className="relative z-10">Configurações da IA</span>
											</button>
										</div>
									</>
								)}

								{settings.AI_SELECTED === "GPT" && (
									<>
										<div>
											<span
												htmlFor="gemini-prompt"
												className="mb-1 block opacity-0 invisible font-medium text-grayLight"
											>
												GPT Prompt
											</span>
											<button
												onClick={() => setGptPromptModalOpen(true)}
												type="button"
												className="w-full relative rounded-lg text-textOnDarkStrong px-4 py-3 transition-all duration-200 transform hover:scale-[1.01] bg-gradient-to-r from-emeraldBase via-cyanPrimary to-tealPrimary hover:shadow-lg hover:shadow-emeraldBase/25 cursor-pointer"
												style={{ cursor: "pointer", pointerEvents: "auto" }}
											>
												<span className="relative z-10">Configurações da IA</span>
											</button>
										</div>
									</>
								)}

								{settings.AI_SELECTED === "PUREROUTER" && (
									<>
										<div>
											<span
												htmlFor="purerouter-prompt"
												className="mb-1 block opacity-0 invisible font-medium text-grayLight"
											>
												oZap AI Prompt
											</span>
											<button
												onClick={() => setPurerouterPromptModalOpen(true)}
												type="button"
												className="w-full relative rounded-lg text-textOnDarkStrong px-4 py-3 transition-all duration-200 transform hover:scale-[1.01] bg-gradient-to-r from-emeraldBase via-cyanPrimary to-tealPrimary hover:shadow-lg hover:shadow-emeraldBase/25 cursor-pointer"
												style={{ cursor: "pointer", pointerEvents: "auto" }}
											>
												<span className="relative z-10">Configurações da IA</span>
											</button>
										</div>
									</>
								)}

								{settings.AI_SELECTED === "CLAUDE" && (
									<>
										<div>
											<span
												className="mb-1 block opacity-0 invisible font-medium text-grayLight"
											>
												Claude Prompt
											</span>
											<button
												onClick={() => setClaudePromptModalOpen(true)}
												type="button"
												className="w-full relative rounded-lg text-textOnDarkStrong px-4 py-3 transition-all duration-200 transform hover:scale-[1.01] bg-gradient-to-r from-emeraldBase via-cyanPrimary to-tealPrimary hover:shadow-lg hover:shadow-emeraldBase/25 cursor-pointer"
												style={{ cursor: "pointer", pointerEvents: "auto" }}
											>
												<span className="relative z-10">Configurações da IA</span>
											</button>
										</div>
									</>
								)}
							</div>
						</div>

						<div className="mb-8 bg-grayDeep rounded-xl p-6 border-2 border-borderPrimary/60 shadow-inner shadow-borderPrimary/10">
							<h2 className="mb-6 text-xl font-semibold text-textHeading flex items-center">
								<MessageSquareText className="mr-2 h-5 w-5" />
								Conversas
							</h2>

							<div className="space-y-5">
								<div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 rounded-xl border border-emeraldMedium/50 shadow-lg hover:shadow-emeraldBase/25 transition-shadow duration-200">
									<button
										type="button"
										className="relative z-10 flex w-full items-center justify-between p-4 cursor-pointer bg-transparent text-left"
										onClick={toggleQuickSettings}
										aria-expanded={showQuickSettings}
									>
										<h3 className="font-medium flex items-center">
											<div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-emeraldBase/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60 mr-3">
												<Settings2 className="h-4 w-4 text-emeraldLight" />
											</div>
											<span className="text-textOnDarkStrong">
												Configurações de Resposta
											</span>
										</h3>
										<div
											className={`transform transition-transform duration-200 ${showQuickSettings ? "rotate-180" : ""}`}
										>
											<ChevronDown
												className="h-5 w-5 text-textOnDarkStrong"
												aria-hidden="true"
											/>
										</div>
									</button>

									<div
										className={`transition-all duration-200 ease-in-out ${showQuickSettings ? "max-h-96 opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}
									>
										<div className="px-4 pb-4">
											<div className="relative z-10 bg-grayBase/80 rounded-lg p-5 space-y-4 shadow-inner border border-emeraldMedium/20 backdrop-blur-sm">
												<label className="flex items-center justify-between gap-3 text-textOnDarkMedium text-sm font-medium cursor-pointer hover:text-textOnDarkStrong transition-colors duration-200">
													<div className="flex items-center">
														<span>Manter não lida ao responder</span>
														<div className="relative ml-1">
															<div className="group">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	A IA responde automaticamente às mensagens sem
																	desmarcá-las como 'visualizadas'. Isso permite que
																	você mantenha o controle de quem entrou em
																	contato, garantindo que as mensagens ainda
																	apareçam como não lidas para você.
																</div>
															</div>
														</div>
													</div>
													<div className="relative">
														<input
															type="checkbox"
															checked={!settings.VISUALIZAR_MENSAGENS}
															onChange={(e) => {
																setSettings((prev) => ({
																	...prev,
																	VISUALIZAR_MENSAGENS: !e.target.checked,
																}));
															}}
															className="sr-only"
														/>
														<div
															className={`w-12 h-6 rounded-full transition-all duration-200 shadow-inner ${!settings.VISUALIZAR_MENSAGENS ? "bg-gradient-to-r from-emeraldBase to-cyanPrimary" : "bg-grayDeep"}`}
														>
															<div
																className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-200 ${!settings.VISUALIZAR_MENSAGENS ? "translate-x-6" : "translate-x-0"}`}
															/>
														</div>
													</div>
												</label>

												<div className="space-y-2">
													<label className={`flex items-center justify-between gap-3 text-sm font-medium transition-colors duration-200 ${settings.TTS_SMART_AUDIO ? 'text-grayMedium cursor-not-allowed' : 'text-textOnDarkMedium cursor-pointer hover:text-textOnDarkStrong'}`}>
														<div className="flex items-center gap-2">
															{settings.TTS_SMART_AUDIO && (
																<span className="text-statusError text-xs">⚠️</span>
															)}
															<span>Enviar resposta em uma mensagem</span>
															{settings.TTS_SMART_AUDIO && (
																<span className="px-2 py-0.5 text-xs bg-redBase/20 text-statusErrorLight rounded-full border border-redBase/40">
																	BLOQUEADA
																</span>
															)}
															<div className="relative ml-1">
																<div className="group">
																	<Info className="size-4 cursor-pointer text-grayMedium" />
																	<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																		{settings.TTS_SMART_AUDIO
																			? '⚠️ Incompatível com "Áudio Inteligente". Desative primeiro para usar esta opção.'
																			: 'Se ativado, a IA enviará a resposta completa de uma vez. Se desativado, enviará a resposta em partes para simular digitação humana.'
																		}
																	</div>
																</div>
															</div>
														</div>
														<div className="relative">
															<input
																type="checkbox"
																checked={settings.ENVIO_EM_BLOCO}
																onChange={handleSettingsChange}
																name="ENVIO_EM_BLOCO"
																disabled={settings.TTS_SMART_AUDIO}
																className="sr-only"
															/>
															<div
																className={`w-12 h-6 rounded-full transition-all duration-300 shadow-inner ${
																	settings.TTS_SMART_AUDIO
																		? 'bg-grayDeep'
																		: settings.ENVIO_EM_BLOCO
																			? "bg-gradient-to-r from-emeraldBase to-cyanPrimary"
																			: "bg-grayDeep"
																}`}
															>
																<div
																	className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${settings.ENVIO_EM_BLOCO ? "translate-x-6" : "translate-x-0"}`}
																/>
															</div>
														</div>
													</label>
													{settings.TTS_SMART_AUDIO && (
														<div className="text-xs text-statusError flex items-center gap-1 ml-6">
															<span>🚫</span>
															<span>Desabilitado: conflita com "Áudio Inteligente"</span>
														</div>
													)}
												</div>

												<label className="flex items-center justify-between gap-3 text-textOnDarkMedium text-sm font-medium cursor-pointer hover:text-textOnDarkStrong transition-colors duration-200">
													<div className="flex items-center">
														<span>Incluir nome do contato na resposta</span>
														<div className="relative ml-1">
															<div className="group">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Se ativado, a IA incluirá o nome do contato na
																	resposta. Contatos sem nome (ex: vindos de anúncios
																	do Meta Ads) serão respondidos normalmente, apenas
																	sem a referência ao nome.
																</div>
															</div>
														</div>
													</div>
													<div className="relative">
														<input
															type="checkbox"
															checked={settings.INCLUIR_NOME_CONTATO}
															onChange={(e) =>
																setSettings((prev) => ({
																	...prev,
																	INCLUIR_NOME_CONTATO: e.target.checked,
																}))
															}
															className="sr-only"
														/>
														<div
															className={`w-12 h-6 rounded-full transition-all duration-300 shadow-inner ${settings.INCLUIR_NOME_CONTATO ? "bg-gradient-to-r from-emeraldBase to-cyanPrimary" : "bg-grayDeep"}`}
														>
															<div
																className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${settings.INCLUIR_NOME_CONTATO ? "translate-x-6" : "translate-x-0"}`}
															/>
														</div>
													</div>
												</label>

												<label className="flex items-center justify-between gap-3 text-textOnDarkMedium text-sm font-medium cursor-pointer hover:text-textOnDarkStrong transition-colors duration-200">
													<div className="flex items-center">
														<span>Cancelar respostas ao receber nova mensagem</span>
														<div className="relative ml-1">
															<div className="group">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Se ativado: Quando a IA está enviando múltiplas mensagens e o usuário envia uma nova mensagem, o sistema cancela a fila antiga e processa o novo contexto imediatamente com resposta 50% mais rápida. Se desativado: A IA enviará todas as mensagens da fila antes de processar a nova mensagem.
																</div>
															</div>
														</div>
													</div>
													<div className="relative">
														<input
															type="checkbox"
															checked={settings.CANCELAR_RESPOSTA_EM_NOVAS_MENSAGENS}
															onChange={(e) =>
																setSettings((prev) => ({
																	...prev,
																	CANCELAR_RESPOSTA_EM_NOVAS_MENSAGENS: e.target.checked,
																}))
															}
															className="sr-only"
														/>
														<div
															className={`w-12 h-6 rounded-full transition-all duration-300 shadow-inner ${settings.CANCELAR_RESPOSTA_EM_NOVAS_MENSAGENS ? "bg-gradient-to-r from-emeraldBase to-cyanPrimary" : "bg-grayDeep"}`}
														>
															<div
																className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${settings.CANCELAR_RESPOSTA_EM_NOVAS_MENSAGENS ? "translate-x-6" : "translate-x-0"}`}
															/>
														</div>
													</div>
												</label>

												<label className="flex items-center justify-between gap-3 text-textOnDarkMedium text-sm font-medium cursor-pointer hover:text-textOnDarkStrong transition-colors duration-200">
													<div className="flex items-center">
														<span>Não responder após mensagem manual</span>
														<div className="relative ml-1">
															<div className="group">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Se ativado: A primeira mensagem manual enviada para um contato fará a IA parar de responder naquele chat. Se desativado: A IA responderá a primeira mensagem de cada novo contato antes de parar. <br /><br />  NÃO recomendado para quem faz disparos de campanhas em massa, pois a IA não continuará as conversas com leads.
																</div>
															</div>
														</div>
													</div>
													<div className="relative">
														<input
															type="checkbox"
															checked={settings.INTERVENCAO_HUMANA_IMEDIATA}
															onChange={(e) =>
																setSettings((prev) => ({
																	...prev,
																	INTERVENCAO_HUMANA_IMEDIATA: e.target.checked,
																}))
															}
															className="sr-only"
														/>
														<div
															className={`w-12 h-6 rounded-full transition-all duration-300 shadow-inner ${settings.INTERVENCAO_HUMANA_IMEDIATA ? "bg-gradient-to-r from-emeraldBase to-cyanPrimary" : "bg-grayDeep"}`}
														>
															<div
																className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${settings.INTERVENCAO_HUMANA_IMEDIATA ? "translate-x-6" : "translate-x-0"}`}
															/>
														</div>
													</div>
												</label>
											</div>
										</div>
									</div>
								</div>

								<div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 rounded-xl border border-emeraldMedium/50 shadow-lg hover:shadow-emeraldBase/25 transition-shadow duration-200">
									<button
										type="button"
										className="relative z-10 flex w-full items-center justify-between p-4 cursor-pointer bg-transparent text-left"
										onClick={toggleTimingSettings}
										aria-expanded={showTimingSettings}
									>
										<h3 className="font-medium flex items-center">
											<div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-emeraldBase/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60 mr-3">
												<Clock className="h-4 w-4 text-emeraldLight" />
											</div>
											<span className="text-textOnDarkStrong">Tempo e Mensagens</span>
										</h3>
										<div
											className={`transform transition-transform duration-200 ${showTimingSettings ? "rotate-180" : ""}`}
										>
											<ChevronDown
												className="h-5 w-5 text-textOnDarkStrong"
												aria-hidden="true"
											/>
										</div>
									</button>

									<div
										className={`transition-all duration-200 ease-in-out ${showTimingSettings ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}
									>
										<div className="px-4 pb-4">
											<div className="relative z-10 bg-grayBase/80 rounded-lg p-5 space-y-5 shadow-inner border border-emeraldMedium/20 backdrop-blur-sm">
												{/* Linha 1: Botão de configurar tempo */}
												<div className="flex justify-start">
													<button
														onClick={() => setShowMessageTimerModal(true)}
														type="button"
														className="rounded-lg bg-gradient-to-r from-emeraldBase to-cyanPrimary px-4 py-2 text-sm font-medium text-textOnDarkStrong transition-all duration-200 hover:shadow-lg hover:shadow-emeraldBase/25"
													>
														Configurar Tempo de Mensagens
													</button>
												</div>

												<div className="flex flex-wrap items-start gap-6">
													<label className="flex items-center gap-3 cursor-pointer">
														<div className="flex items-center">
															<span className="text-textOnDarkMedium text-sm font-medium">Intervenção Humana</span>
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Quando ativado, a IA para de responder automaticamente ao detectar que você enviou uma mensagem manual no chat. A IA volta a responder após o tempo de reativação configurado.
																</div>
															</div>
														</div>
														<div className="relative">
															<input
																type="checkbox"
																checked={settings.INTERVENCAO_HUMANA_ATIVA}
																onChange={(e) =>
																	setSettings((prev) => ({
																		...prev,
																		INTERVENCAO_HUMANA_ATIVA: e.target.checked,
																	}))
																}
																className="sr-only"
															/>
															<div
																className={`w-12 h-6 rounded-full transition-all duration-300 shadow-inner ${settings.INTERVENCAO_HUMANA_ATIVA ? "bg-gradient-to-r from-emeraldBase to-cyanPrimary" : "bg-grayDeep"}`}
															>
																<div
																	className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${settings.INTERVENCAO_HUMANA_ATIVA ? "translate-x-6" : "translate-x-0"}`}
																/>
															</div>
														</div>
													</label>

													<div className="flex flex-col">
														<label
															className="flex items-center font-medium text-textOnDarkMedium text-sm mb-1"
															htmlFor="HORAS_PARA_REATIVAR_IA"
														>
															Reativação (hrs)
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Define após quantas horas o bot pode voltar a
																	responder a um contato que ele já ignorou.
																</div>
															</div>
														</label>
														<input
															type="number"
															name="HORAS_PARA_REATIVAR_IA"
															value={settings.HORAS_PARA_REATIVAR_IA}
															onChange={handleSettingsChange}
															disabled={!settings.INTERVENCAO_HUMANA_ATIVA}
															className={`w-24 rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 text-center backdrop-blur-sm ${!settings.INTERVENCAO_HUMANA_ATIVA ? "opacity-50 cursor-not-allowed" : ""}`}
														/>
													</div>
												</div>

												<div>
													<label
														className="mb-2 flex items-center font-medium text-textOnDarkMedium text-sm"
														htmlFor="MENSAGEM_PARA_ENVIAR_QUANDO_RECEBER_TIPO_DESCONHECIDO"
													>
														Mensagem para tipos desconhecidos
														<div className="group relative ml-1">
															<Info className="size-4 cursor-pointer text-grayMedium" />
															<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																Se o bot receber uma mensagem que não sabe como
																responder, ele usará essa resposta padrão.
															</div>
														</div>
													</label>
													<input
														type="text"
														name="MENSAGEM_PARA_ENVIAR_QUANDO_RECEBER_TIPO_DESCONHECIDO"
														value={
															settings.MENSAGEM_PARA_ENVIAR_QUANDO_RECEBER_TIPO_DESCONHECIDO
														}
														onChange={handleSettingsChange}
														className="w-full rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
													/>
												</div>

												<div>
													<label
														className="mb-2 flex items-center font-medium text-textOnDarkMedium text-sm"
														htmlFor="timezone"
													>
														Fuso Horário
														<div className="group relative ml-1">
															<Info className="size-4 cursor-pointer text-grayMedium" />
															<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																Define o fuso horário usado para timestamps da IA e agendamentos do Google Calendar. Útil para usuários em diferentes países.
															</div>
														</div>
													</label>
													<select
														name="timezone"
														value={settings.timezone || "America/Sao_Paulo"}
														onChange={handleSettingsChange}
														className="w-full rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
													>
														<option value="America/Sao_Paulo">Brasil - São Paulo (GMT-3)</option>
														<option value="America/Manaus">Brasil - Manaus (GMT-4)</option>
														<option value="America/Recife">Brasil - Recife (GMT-3)</option>
														<option value="America/New_York">EUA - Nova York (GMT-5)</option>
														<option value="America/Los_Angeles">EUA - Los Angeles (GMT-8)</option>
														<option value="America/Chicago">EUA - Chicago (GMT-6)</option>
														<option value="America/Denver">EUA - Denver (GMT-7)</option>
														<option value="America/Toronto">Canadá - Toronto (GMT-5)</option>
														<option value="America/Vancouver">Canadá - Vancouver (GMT-8)</option>
														<option value="America/Mexico_City">México - Cidade do México (GMT-6)</option>
														<option value="America/Argentina/Buenos_Aires">Argentina - Buenos Aires (GMT-3)</option>
														<option value="America/Bogota">Colômbia - Bogotá (GMT-5)</option>
														<option value="America/Caracas">Venezuela - Caracas (GMT-4)</option>
														<option value="America/Lima">Peru - Lima (GMT-5)</option>
														<option value="America/Santiago">Chile - Santiago (GMT-3)</option>
														<option value="America/Havana">Cuba - Havana (GMT-5)</option>
														<option value="America/Panama">Panamá - Cidade do Panamá (GMT-5)</option>
														<option value="Europe/Lisbon">Portugal - Lisboa (GMT+0)</option>
														<option value="Europe/London">Reino Unido - Londres (GMT+0)</option>
														<option value="Europe/Paris">França - Paris (GMT+1)</option>
														<option value="Europe/Berlin">Alemanha - Berlim (GMT+1)</option>
														<option value="Europe/Madrid">Espanha - Madri (GMT+1)</option>
														<option value="Europe/Rome">Itália - Roma (GMT+1)</option>
														<option value="Asia/Kolkata">Índia - Kolkata (GMT+5:30)</option>
														<option value="Asia/Jakarta">Indonésia - Jakarta (GMT+7)</option>
														<option value="Asia/Tokyo">Japão - Tóquio (GMT+9)</option>
														<option value="Asia/Shanghai">China - Xangai (GMT+8)</option>
														<option value="Asia/Dubai">Emirados Árabes - Dubai (GMT+4)</option>
														<option value="Africa/Cairo">Egito - Cairo (GMT+2)</option>
														<option value="Africa/Johannesburg">África do Sul - Johannesburgo (GMT+2)</option>
														<option value="Africa/Lagos">Nigéria - Lagos (GMT+1)</option>
														<option value="Australia/Sydney">Austrália - Sydney (GMT+10)</option>
													</select>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 rounded-xl border border-emeraldMedium/50 shadow-lg hover:shadow-emeraldBase/25 transition-all duration-300">
									<div className="absolute inset-0 bg-gradient-to-br from-emeraldMedium/10 via-cyanMedium/10 to-tealMedium/10 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
									<div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>

									<button
										type="button"
										className="relative z-10 flex w-full items-center justify-between p-4 cursor-pointer bg-transparent text-left"
										onClick={toggleAlertSettings}
										aria-expanded={showAlertSettings}
									>
										<h3 className="font-medium flex items-center">
											<div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-emeraldBase/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60 mr-3">
												<BellRing className="h-4 w-4 text-emeraldLight" />
											</div>
											<span className="text-textOnDarkStrong">Alerta de atendimento humano</span>
											<span className="ml-2 px-2 py-0.5 text-xs bg-cyanPrimary/20 text-cyanLight rounded-full border border-cyanPrimary/40">
												NOVO
											</span>
										</h3>
										<div
											className={`transform transition-transform duration-200 ${showAlertSettings ? "rotate-180" : ""}`}
										>
											<ChevronDown
												className="h-5 w-5 text-textOnDarkStrong"
												aria-hidden="true"
											/>
										</div>
									</button>

									<div
										className={`transition-all duration-200 ease-in-out ${showAlertSettings ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}
									>
										<div className="px-4 pb-4">
											<div className="relative z-10 bg-grayBase/80 rounded-lg p-5 space-y-5 shadow-inner border border-emeraldMedium/20 backdrop-blur-sm">
												<div className="flex flex-col gap-2">
													<div className="flex items-center justify-between">
														<div className="flex items-center">
															<span className="text-textOnDarkMedium text-sm font-medium">Enviar alertas por WhatsApp</span>
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Ative para enviar alertas por WhatsApp para os números cadastrados quando uma conversa precisar de intervenção humana ou ocorrer erro crítico.
																</div>
															</div>
														</div>
														<label className="relative inline-flex items-center cursor-pointer">
															<input
																type="checkbox"
																checked={!!settings.ALERT_ENABLED}
																onChange={(e) => {
																	if (e.target.checked && alertNumbersArray.length === 0) {
																		toast.info('Adicione um número para receber alertas');
																	}
																	const newVal = e.target.checked;
																	setSettings((prev) => ({ ...prev, ALERT_ENABLED: newVal }));
																	ipcRenderer.invoke('save-setting', { instanceId, key: 'ALERT_ENABLED', value: newVal }).catch(err => {
																		console.error('Erro ao salvar ALERT_ENABLED', err);
																		toast.error('Erro ao salvar configuração de alertas');
																	});
																}}
																className="sr-only peer"
															/>
															<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep"></div>
														</label>
													</div>
													<div className="w-full rounded-lg border border-emeraldMedium/30 bg-grayBase/50 px-4 py-3 max-h-60 overflow-y-auto backdrop-blur-sm">
															<div className="mb-3 flex">
																<input
																	type="text"
																	value={newAlertNumber}
																	onChange={(e) => {
																		const sanitized = sanitizePhoneNumber(e.target.value);
																		setNewAlertNumber(sanitized);
																	}}
																	className="grow rounded-l-lg border border-emeraldMedium/30 bg-grayBase/80 px-3 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
																	placeholder="Adicionar número (ex: 551188888888)"
																/>
																<button
																	type="button"
																	onClick={async () => {
																		const cleaned = sanitizePhoneNumber(newAlertNumber.trim());
																		if (!cleaned || cleaned.length < 10) {
																			toast.error('Número inválido (mínimo 10 dígitos)');
																			return;
																		}
																		if (alertNumbersArray.includes(cleaned)) {
																			toast.error('Número já adicionado');
																			return;
																		}
																		const newNumbers = [...alertNumbersArray, cleaned];
																		const newValue = newNumbers.join(',');
																		try {
																			await ipcRenderer.invoke('save-setting', { instanceId, key: 'ALERT_NUMBER', value: newValue });
																			await ipcRenderer.invoke('save-setting', { instanceId, key: 'ALERT_ENABLED', value: true });
																			setSettings(prev => ({ ...prev, ALERT_NUMBER: newValue, ALERT_ENABLED: true }));
																			setNewAlertNumber("");
																			toast.success('Número adicionado');
																		} catch (err) {
																			console.error('Erro ao salvar número de alerta', err);
																			toast.error('Erro ao salvar');
																		}
																	}}
																	className="rounded-r-lg bg-gradient-to-r from-emeraldBase to-cyanPrimary px-3 py-2 text-textOnDarkStrong transition hover:shadow-md hover:shadow-emeraldBase/25"
																	title="Adicionar"
																>
																	<Plus className="h-5 w-5" />
																</button>
															</div>
															{alertNumbersArray.length === 0 && (
																<div className="text-grayMedium text-center text-sm py-2">
																	Nenhum número adicionado
																</div>
															)}
															{alertNumbersArray.map((number, index) => (
																<div
																	key={number}
																	className="flex items-center justify-between mb-2 px-3 py-2 bg-grayDeep/50 rounded-lg border border-emeraldMedium/10 hover:border-emeraldMedium/30 transition-colors"
																>
																	<span className="text-textOnDarkStrong">{number}</span>
																	<button
																		type="button"
																		onClick={async () => {
																			const newNumbers = alertNumbersArray.filter((_, i) => i !== index);
																			const newValue = newNumbers.join(',');
																			try {
																				await ipcRenderer.invoke('save-setting', { instanceId, key: 'ALERT_NUMBER', value: newValue });
																				setSettings(prev => ({ ...prev, ALERT_NUMBER: newValue }));
																				if (newNumbers.length === 0) {
																					await ipcRenderer.invoke('save-setting', { instanceId, key: 'ALERT_ENABLED', value: false });
																					setSettings(prev => ({ ...prev, ALERT_ENABLED: false }));
																				}
																				toast.success('Número removido');
																			} catch (err) {
																				console.error('Erro ao remover número de alerta', err);
																				toast.error('Erro ao remover');
																			}
																		}}
																		className="ml-4 bg-transparent rounded-full border border-redBase/50 p-1 text-statusError hover:bg-redBase/20 transition-all"
																	>
																		<TrashIcon className="h-4 w-4" />
																	</button>
																</div>
															))}
														</div>
												</div>

												<div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-emeraldMedium/20">
													<div className="flex items-center justify-between p-3 rounded-lg bg-grayDeep/30 border border-emeraldMedium/10">
														<div className="flex items-center">
															<span className="text-textOnDarkMedium text-sm font-medium">Parar IA após pedir atendimento</span>
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Quando a IA solicitar atendimento humano, ela para de responder automaticamente. Se desativado, a IA continua respondendo mesmo após solicitar atendimento.
																</div>
															</div>
														</div>
														<label className="relative inline-flex items-center cursor-pointer">
															<input
																type="checkbox"
																checked={settings.PARAR_IA_APOS_PEDIR_INTERVENCAO}
																onChange={(e) =>
																	setSettings((prev) => ({
																		...prev,
																		PARAR_IA_APOS_PEDIR_INTERVENCAO: e.target.checked,
																	}))
																}
																className="sr-only peer"
															/>
															<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep"></div>
														</label>
													</div>

													<div className={`flex items-center justify-between p-3 rounded-lg bg-grayDeep/30 border border-emeraldMedium/10 ${!settings.PARAR_IA_APOS_PEDIR_INTERVENCAO ? "opacity-50" : ""}`}>
														<div className="flex items-center">
															<span className="text-textOnDarkMedium text-sm font-medium">Duração da pausa</span>
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Por quanto tempo a IA ignora a conversa após solicitar atendimento humano.
																</div>
															</div>
														</div>
														<select
															name="INTERVENCAO_DURACAO_HORAS"
															id="INTERVENCAO_DURACAO_HORAS"
															value={settings.INTERVENCAO_DURACAO_HORAS}
															onChange={handleSettingsChange}
															disabled={!settings.PARAR_IA_APOS_PEDIR_INTERVENCAO}
															className={`w-32 rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-3 py-1.5 text-sm text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm ${!settings.PARAR_IA_APOS_PEDIR_INTERVENCAO ? "cursor-not-allowed" : ""}`}
														>
															<option value="1">1 hora</option>
															<option value="6">6 horas</option>
															<option value="12">12 horas</option>
															<option value="24">24 horas</option>
															<option value="48">48 horas</option>
															<option value="72">72 horas</option>
															<option value="0">Para sempre</option>
														</select>
													</div>

													<div className="flex items-center justify-between p-3 rounded-lg bg-grayDeep/30 border border-emeraldMedium/10 md:col-span-2">
														<div className="flex items-center">
															<span className="text-textOnDarkMedium text-sm font-medium">Incluir resumo da conversa</span>
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	A IA gera um resumo curto da conversa (até 200 caracteres) para contextualizar o alerta.
																</div>
															</div>
														</div>
														<label className="relative inline-flex items-center cursor-pointer">
															<input
																type="checkbox"
																checked={settings.ALERT_INCLUDE_SUMMARY}
																onChange={(e) =>
																	setSettings((prev) => ({
																		...prev,
																		ALERT_INCLUDE_SUMMARY: e.target.checked,
																	}))
																}
																className="sr-only peer"
															/>
															<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep"></div>
														</label>
													</div>
												</div>

												<div className="pt-4 border-t border-emeraldMedium/20">
													<div className="flex items-center justify-between p-3 rounded-lg bg-grayDeep/30 border border-emeraldMedium/10">
														<div className="flex items-center">
															<span className="text-textOnDarkMedium text-sm font-medium">Usar regras customizadas para intervenção</span>
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-80 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Ative para personalizar as regras de quando a IA deve solicitar intervenção humana. Desativado, usa a regra padrão (transferir quando cliente pedir explicitamente).
																</div>
															</div>
														</div>
														<label className="relative inline-flex items-center cursor-pointer">
															<input
																type="checkbox"
																checked={settings.ALERT_USE_CUSTOM_PROMPT}
																onChange={(e) => {
																	const isChecked = e.target.checked;
																	const defaultInterventionPrompt = `Quando o cliente solicitar EXPLICITAMENTE falar com um humano/atendente/pessoa real, você DEVE OBRIGATORIAMENTE chamar a função "request_human_intervention" IMEDIATAMENTE, ANTES de responder.
Exemplos de quando usar: "quero falar com humano", "me passa para um atendente", "preciso de ajuda humana", "quero falar com uma pessoa", "pode me transferir", "quero atendimento humano".
- SEMPRE chame a função request_human_intervention nesses casos
- NÃO peça confirmação de dados antes de chamar a função
- Informe ao cliente que ele será transferido APÓS chamar a função`;
																	setSettings((prev) => ({
																		...prev,
																		ALERT_USE_CUSTOM_PROMPT: isChecked,
																		...(isChecked && !prev.ALERT_CUSTOM_PROMPT?.trim() ? { ALERT_CUSTOM_PROMPT: defaultInterventionPrompt } : {})
																	}));
																}}
																className="sr-only peer"
															/>
															<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep"></div>
														</label>
													</div>

													{settings.ALERT_USE_CUSTOM_PROMPT && (
														<div className="mt-3">
															<textarea
																name="ALERT_CUSTOM_PROMPT"
																id="ALERT_CUSTOM_PROMPT"
																value={settings.ALERT_CUSTOM_PROMPT || ""}
																onChange={handleSettingsChange}
																placeholder="Defina quando a IA deve acionar intervenção humana..."
																rows={5}
																className="w-full rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-3 py-2 text-sm text-textOnDarkStrong placeholder:text-grayMedium focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm resize-none"
															/>
															<p className="text-xs text-grayMedium mt-1">Edite as regras acima para personalizar quando a IA deve solicitar atendimento humano.</p>
														</div>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 rounded-xl border border-emeraldMedium/50 shadow-lg hover:shadow-emeraldBase/25 transition-all duration-300">
									<div className="absolute inset-0 bg-gradient-to-br from-emeraldMedium/10 via-cyanMedium/10 to-tealMedium/10 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
									<div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>

									<button
										type="button"
										className="relative z-10 flex w-full items-center justify-between p-4 cursor-pointer bg-transparent text-left"
										onClick={toggleNumberFilters}
										aria-expanded={showNumberFilters}
									>
										<h3 className="font-medium flex items-center">
											<div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-emeraldBase/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60 mr-3">
												<Filter className="h-4 w-4 text-emeraldLight" />
											</div>
											<span className="text-textOnDarkStrong">Filtros de Número</span>
										</h3>
										<div
											className={`transform transition-transform duration-300 ${showNumberFilters ? "rotate-180" : ""}`}
										>
											<ChevronDown
												className="h-5 w-5 text-textOnDarkStrong"
												aria-hidden="true"
											/>
										</div>
									</button>

									{/* Conteúdo do painel - Filtros de Números */}
									<div
										className={`transition-all duration-300 ease-in-out ${showNumberFilters ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}
									>
										<div className="px-4 pb-4">
											<div className="relative z-10 bg-grayBase/80 rounded-lg p-5 shadow-inner border border-emeraldMedium/20 backdrop-blur-sm">
												<div className="flex w-full gap-5">
													<div className="w-1/2">
												<div className="flex items-center justify-between mb-2">
													<label
														htmlFor="only-respond-to"
														className="flex items-center font-medium text-textOnDarkMedium text-sm"
													>
														Somente Responder
														<div className="group relative ml-1">
															<Info className="size-4 cursor-pointer text-grayMedium" />
															<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																Se você deseja que a IA responda apenas a
																contatos específicos, insira aqui os números
																neste formato: (por exemplo: "555181995600").
															</div>
														</div>
													</label>
													<div className="flex gap-2">
														<button
															type="button"
															onClick={() => handleExportNumbers('SOMENTE_RESPONDER')}
															disabled={exportingNumbers || !settings.SOMENTE_RESPONDER?.length}
															className="p-1.5 rounded-lg bg-emeraldBase/20 text-emeraldLight hover:bg-emeraldBase/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
															title="Exportar números"
														>
															<Download className="h-4 w-4" />
														</button>
														<button
															type="button"
															onClick={() => handleImportNumbers('SOMENTE_RESPONDER')}
															disabled={importingNumbers}
															className="p-1.5 rounded-lg bg-cyanPrimary/20 text-cyanLight hover:bg-cyanPrimary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
															title="Importar números"
														>
															<Upload className="h-4 w-4" />
														</button>
													</div>
												</div>
																		<div className="w-full rounded-lg border border-emeraldMedium/30 bg-grayBase/50 px-4 py-3 max-h-60 overflow-y-auto backdrop-blur-sm">
																			<div className="mb-3 flex">
																				<input
																						id="add-only-respond-to"
																						type="text"
																						value={newSomenteResponder}
																						onChange={(e) => {
																							const value = e.target.value;
																							const sanitizedValue = sanitizePhoneNumber(value);
																							setNewSomenteResponder(sanitizedValue);
																						}}
																						className="grow rounded-l-lg border border-emeraldMedium/30 bg-grayBase/80 px-3 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
																						placeholder="Adicionar número"
																					/>
																				<button
																					type="button"
																					onClick={() => {
																						if (newSomenteResponder.trim() !== "") {
																							setSettings((prevSettings) => ({
																								...prevSettings,
																								SOMENTE_RESPONDER: [
																									...(prevSettings.SOMENTE_RESPONDER || []),
																									newSomenteResponder.trim(),
																								],
																							}));
																							setNewSomenteResponder("");
																						}
																					}}
																					className="rounded-r-lg bg-gradient-to-r from-emeraldBase to-cyanPrimary px-3 py-2 text-textOnDarkStrong transition hover:shadow-md hover:shadow-emeraldBase/25"
																					title="Adicionar"
																				>
																					<Plus className="h-5 w-5" />
																				</button>
																			</div>
																			{(!settings.SOMENTE_RESPONDER || settings.SOMENTE_RESPONDER.length === 0) && (
																				<div className="text-grayMedium text-center text-sm py-2">
																					Nenhum número adicionado
																				</div>
																			)}
																			{(settings.SOMENTE_RESPONDER || []).map((number, index) => (
																				<div
																					key={number}
																					className="flex items-center justify-between mb-2 px-3 py-2 bg-grayDeep/50 rounded-lg border border-emeraldMedium/10 hover:border-emeraldMedium/30 transition-colors"
																				>
																					<span>{number}</span>
																					<button
																						type="button"
																						onClick={() => {
																							setSettings((prevSettings) => ({
																								...prevSettings,
																								SOMENTE_RESPONDER:
																									(prevSettings.SOMENTE_RESPONDER || []).filter(
																										(_, i) => i !== index,
																									),
																							}));
																						}}
																						className="ml-4 bg-transparent rounded-full border border-redBase/50 p-1 text-statusError hover:bg-redBase/20 transition-all"
																					>
																						<TrashIcon className="h-4 w-4" />
																					</button>
																				</div>
																			))}
																		</div>
																</div>													<div className="w-1/2">
												<div className="flex items-center justify-between mb-2">
													<label
														htmlFor="dont-respond-to"
														className="flex items-center font-medium text-textOnDarkMedium text-sm"
													>
														Não Responder
														<div className="group relative ml-1">
															<Info className="size-4 cursor-pointer text-grayMedium" />
															<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																Se você deseja que a IA não responda a certos
																contatos, insira aqui os números separados por
																vírgula (por exemplo: "555181995600").
															</div>
														</div>
													</label>
													<div className="flex gap-2">
														<button
															type="button"
															onClick={() => handleExportNumbers('NAO_RESPONDER')}
															disabled={exportingNumbers || !settings.NAO_RESPONDER?.length}
															className="p-1.5 rounded-lg bg-emeraldBase/20 text-emeraldLight hover:bg-emeraldBase/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
															title="Exportar números"
														>
															<Download className="h-4 w-4" />
														</button>
														<button
															type="button"
															onClick={() => handleImportNumbers('NAO_RESPONDER')}
															disabled={importingNumbers}
															className="p-1.5 rounded-lg bg-cyanPrimary/20 text-cyanLight hover:bg-cyanPrimary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
															title="Importar números"
														>
															<Upload className="h-4 w-4" />
														</button>
													</div>
												</div>
														<div className="w-full rounded-lg border border-emeraldMedium/30 bg-grayBase/50 px-4 py-3 max-h-60 overflow-y-auto backdrop-blur-sm">
															<div className="mb-3 flex">
																<input
																		id="add-dont-respond-to"
																		type="text"
																		value={newNaoResponder}
																		onChange={(e) => {
																			const value = e.target.value;
																			const sanitizedValue = sanitizePhoneNumber(value);
																			setNewNaoResponder(sanitizedValue);
																		}}
																		className="grow rounded-l-lg border border-emeraldMedium/30 bg-grayBase/80 px-3 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
																		placeholder="Adicionar número"
																	/>
																<button
																	type="button"
																	onClick={() => {
																		if (newNaoResponder.trim() !== "") {
																			setSettings((prevSettings) => ({
																				...prevSettings,
																				NAO_RESPONDER: [
																					...(prevSettings.NAO_RESPONDER || []),
																					newNaoResponder.trim(),
																				],
																			}));
																			setNewNaoResponder("");
																		}
																	}}
																	className="rounded-r-lg bg-gradient-to-r from-emeraldBase to-cyanPrimary px-3 py-2 text-textOnDarkStrong transition hover:shadow-md hover:shadow-emeraldBase/25"
																	title="Adicionar"
																>
																	<Plus className="h-5 w-5" />
																</button>
															</div>
															{(!settings.NAO_RESPONDER || settings.NAO_RESPONDER.length === 0) && (
																<div className="text-grayMedium text-center text-sm py-2">
																	Nenhum número adicionado
																</div>
															)}
															{(settings.NAO_RESPONDER || []).map((number, index) => (
																<div
																	key={number}
																	className="flex items-center justify-between mb-2 px-3 py-2 bg-grayDeep/50 rounded-lg border border-emeraldMedium/10 hover:border-emeraldMedium/30 transition-colors"
																>
																	<span>{number}</span>
																	<button
																		type="button"
																		onClick={() => {
																			setSettings((prevSettings) => ({
																				...prevSettings,
																				NAO_RESPONDER:
																					(prevSettings.NAO_RESPONDER || []).filter(
																						(_, i) => i !== index,
																					),
																			}));
																		}}
																		className="ml-4 rounded-full bg-transparent border border-redBase/50 p-1 text-statusError hover:bg-redBase/20 transition-all"
																	>
																		<TrashIcon className="h-4 w-4" />
																	</button>
																</div>
															))}
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* Nova seção de Follow-Up */}
								<div className="relative overflow-hidden bg-gradient-to-br from-emeraldBase/25 via-cyanPrimary/20 to-tealPrimary/25 rounded-xl border border-emeraldMedium/50 shadow-lg hover:shadow-emeraldBase/25 transition-all duration-300">
									<div className="absolute inset-0 bg-gradient-to-br from-emeraldMedium/10 via-cyanMedium/10 to-tealMedium/10 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
									<div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>

									<button
										type="button"
										className="relative z-10 flex w-full items-center justify-between p-4 cursor-pointer bg-transparent text-left"
										onClick={toggleFollowUpSettings}
										aria-expanded={showFollowUpSettings}
									>
										<h3 className="font-medium flex items-center">
											<div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-emeraldBase/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60 mr-3">
												<Bell className="h-4 w-4 text-emeraldLight" />
											</div>
											<span className="text-textOnDarkStrong">Mensagens de Follow-Up</span>
										</h3>
										<div
											className={`transform transition-transform duration-300 ${showFollowUpSettings ? "rotate-180" : ""}`}
										>
											<ChevronDown
												className="h-5 w-5 text-textOnDarkStrong"
												aria-hidden="true"
											/>
										</div>
									</button>

									{/* Conteúdo do painel - Follow-Up */}
									<div
										className={`transition-all duration-300 ease-in-out ${showFollowUpSettings ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}
									>
										<div className="px-4 pb-4">
											<div className="relative z-10 bg-grayBase/80 rounded-lg p-5 space-y-5 shadow-inner border border-emeraldMedium/20 backdrop-blur-sm">
												{/* Ativar Follow-Up */}
												<label className="flex items-center justify-between cursor-pointer">
													<div className="flex items-center">
														<span className="text-textOnDarkStrong font-medium mr-2">Ativar Follow-Up</span>
														<div className="group relative">
															<Info className="size-4 cursor-pointer text-grayMedium" />
															<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																Sistema enviará automaticamente mensagens de follow-up para leads que pararam de responder.
															</div>
														</div>
													</div>
													<div className="relative">
														<input
															type="checkbox"
															checked={settings.FOLLOW_UP_ATIVO}
															onChange={(e) =>
																setSettings((prev) => ({
																	...prev,
																	FOLLOW_UP_ATIVO: e.target.checked,
																}))
															}
															className="sr-only"
														/>
														<div
																															className={`w-12 h-6 rounded-full transition-all duration-300 shadow-inner ${settings.FOLLOW_UP_ATIVO ? "bg-gradient-to-r from-emeraldBase to-cyanPrimary" : "bg-grayDeep"}`}
														>
															<div
																className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${settings.FOLLOW_UP_ATIVO ? "translate-x-6" : "translate-x-0"}`}
															/>
														</div>
													</div>
												</label>

												{/* Gerar por IA */}
												<label className="flex items-center justify-between cursor-pointer">
													<div className="flex items-center">
														<span className="text-textOnDarkStrong font-medium mr-2">Gerar mensagens por IA</span>
														<div className="group relative">
															<Info className="size-4 cursor-pointer text-grayMedium" />
															<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																IA analisará cada conversa e gerará mensagens personalizadas baseadas no contexto.
															</div>
														</div>
													</div>
													<div className="relative">
														<input
															type="checkbox"
															checked={settings.FOLLOW_UP_GERAR_IA}
															onChange={(e) =>
																setSettings((prev) => ({
																	...prev,
																	FOLLOW_UP_GERAR_IA: e.target.checked,
																}))
															}
															className="sr-only"
														/>
														<div
																															className={`w-12 h-6 rounded-full transition-all duration-300 shadow-inner ${settings.FOLLOW_UP_GERAR_IA ? "bg-gradient-to-r from-emeraldBase to-cyanPrimary" : "bg-grayDeep"}`}
														>
															<div
																className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${settings.FOLLOW_UP_GERAR_IA ? "translate-x-6" : "translate-x-0"}`}
															/>
														</div>
													</div>
												</label>

												{/* Botão para editar prompt - só aparece quando Gerar por IA está ativo */}
												{settings.FOLLOW_UP_GERAR_IA && (
													<div className="flex items-center justify-between">
														<div className="flex items-center">
															<span className="text-grayMedium text-sm">Personalizar prompt da IA</span>
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	O prompt padrão já está otimizado para gerar excelentes mensagens de follow-up.
																	Só modifique se souber exatamente o que está fazendo ou quiser adaptar ao seu negócio específico.
																</div>
															</div>
														</div>
														<button
															type="button"
															onClick={() => setShowFollowUpPromptModal(true)}
															className="px-3 py-1.5 bg-transparent text-sm rounded-lg border border-emeraldMedium/30 text-emeraldLight hover:bg-emeraldBase/10 transition-colors flex items-center"
														>
															<Wand2 className="w-4 h-4 mr-1.5" />
															Editar Prompt
														</button>
													</div>
												)}

												{/* Configurações de tempo */}
												<div className="grid grid-cols-3 gap-4">
													<div className="flex flex-col">
														<label
															className="flex items-center font-medium text-textOnDarkMedium text-sm mb-1"
															htmlFor="FOLLOW_UP_QUANTIDADE_MENSAGENS"
														>
															Qtd mensagens
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-64 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Quantas mensagens de follow-up enviar (máximo 3).
																</div>
															</div>
														</label>
														<select
															name="FOLLOW_UP_QUANTIDADE_MENSAGENS"
															id="FOLLOW_UP_QUANTIDADE_MENSAGENS"
															value={settings.FOLLOW_UP_QUANTIDADE_MENSAGENS}
															onChange={handleSettingsChange}
															className="rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
														>
															<option value="1">1 mensagem</option>
															<option value="2">2 mensagens</option>
															<option value="3">3 mensagens</option>
														</select>
													</div>

													<div className="flex flex-col">
														<label
															className="flex items-center font-medium text-textOnDarkMedium text-sm mb-1"
															htmlFor="FOLLOW_UP_TEMPO_VERIFICACAO"
														>
															Verificação (min)
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-64 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Tempo para verificar se precisa de follow-up após receber mensagem.
																</div>
															</div>
														</label>
														<input
															type="number"
															name="FOLLOW_UP_TEMPO_VERIFICACAO"
															id="FOLLOW_UP_TEMPO_VERIFICACAO"
															min="1"
															max="120"
															value={settings.FOLLOW_UP_TEMPO_VERIFICACAO}
															onChange={handleSettingsChange}
															className="rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
														/>
													</div>

													<div className="flex flex-col">
														<label
															className="flex items-center font-medium text-textOnDarkMedium text-sm mb-1"
															htmlFor="FOLLOW_UP_INTERVALO_HORAS"
														>
															Intervalo entre mensagens (hrs)
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-64 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Tempo entre o envio de cada mensagem dentro do mesmo ciclo de follow-up.
																	Exemplo: com 24h e 3 mensagens = 1ª após 10min, 2ª após 24h, 3ª após 48h.
																</div>
															</div>
														</label>
														<input
															type="number"
															name="FOLLOW_UP_INTERVALO_HORAS"
															id="FOLLOW_UP_INTERVALO_HORAS"
															min="1"
															max="168"
															value={settings.FOLLOW_UP_INTERVALO_HORAS}
															onChange={handleSettingsChange}
															className="rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
														/>
													</div>
												</div>

												{/* Configuração de horário de envio */}
												<div className="space-y-3 mt-4">
													<label className="flex items-center justify-between cursor-pointer">
														<div className="flex items-center">
															<span className="text-textOnDarkMedium text-sm font-medium">Respeitar horário comercial</span>
															<div className="group relative ml-1">
																<Info className="size-4 cursor-pointer text-grayMedium" />
																<div className="absolute bottom-full left-1/2 mb-2 hidden w-72 -translate-x-1/2 rounded-md bg-tooltipBg backdrop-blur-md px-3 py-2 text-xs text-tooltipText group-hover:block transition-all duration-200 z-50 shadow-lg border border-tooltipBorder">
																	Quando ativado, follow-ups só serão enviados dentro do horário configurado. Se um follow-up for agendado fora do horário, ele será automaticamente reagendado para o próximo horário permitido.
																</div>
															</div>
														</div>
														<div className="relative">
															<input
																type="checkbox"
																checked={settings.FOLLOW_UP_HORARIO_ATIVO}
																onChange={(e) =>
																	setSettings((prev) => ({
																		...prev,
																		FOLLOW_UP_HORARIO_ATIVO: e.target.checked,
																	}))
																}
																className="sr-only"
															/>
															<div
																className={`w-12 h-6 rounded-full transition-all duration-300 shadow-inner ${settings.FOLLOW_UP_HORARIO_ATIVO ? "bg-gradient-to-r from-emeraldBase to-cyanPrimary" : "bg-grayDeep"}`}
															>
																<div
																	className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${settings.FOLLOW_UP_HORARIO_ATIVO ? "translate-x-6" : "translate-x-0"}`}
																/>
															</div>
														</div>
													</label>

													{settings.FOLLOW_UP_HORARIO_ATIVO && (
														<div className="grid grid-cols-2 gap-4 mt-2">
															<div className="flex flex-col">
																<label className="text-xs text-grayMedium mb-1" htmlFor="FOLLOW_UP_HORARIO_INICIO">
																	Início do horário
																</label>
																<input
																	type="time"
																	name="FOLLOW_UP_HORARIO_INICIO"
																	id="FOLLOW_UP_HORARIO_INICIO"
																	value={settings.FOLLOW_UP_HORARIO_INICIO}
																	onChange={handleSettingsChange}
																	className="rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm [color-scheme:dark]"
																/>
															</div>
															<div className="flex flex-col">
																<label className="text-xs text-grayMedium mb-1" htmlFor="FOLLOW_UP_HORARIO_FIM">
																	Fim do horário
																</label>
																<input
																	type="time"
																	name="FOLLOW_UP_HORARIO_FIM"
																	id="FOLLOW_UP_HORARIO_FIM"
																	value={settings.FOLLOW_UP_HORARIO_FIM}
																	onChange={handleSettingsChange}
																	className="rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm [color-scheme:dark]"
																/>
															</div>
															<div className="col-span-2 text-xs text-grayMedium">
																Follow-ups fora deste horário serão automaticamente reagendados para {settings.FOLLOW_UP_HORARIO_INICIO || "08:00"} do próximo dia. Fuso: {settings.timezone || "America/Sao_Paulo"}
															</div>
														</div>
													)}
												</div>

												{/* Mensagens pré-definidas (só aparece se IA não estiver ativa) */}
												{!settings.FOLLOW_UP_GERAR_IA && (
													<div className="space-y-3">
														<div className="text-textOnDarkMedium text-sm font-medium">
															Mensagens de Follow-Up
														</div>

														{/* Mensagem 1 - sempre aparece */}
														<div className="flex flex-col">
															<label className="text-xs text-grayMedium mb-1" htmlFor="FOLLOW_UP_MENSAGEM_1">Mensagem 1</label>
															<textarea
																id="FOLLOW_UP_MENSAGEM_1"
																name="FOLLOW_UP_MENSAGEM_1"
																value={settings.FOLLOW_UP_MENSAGEM_1}
																onChange={handleSettingsChange}
																rows="2"
																placeholder="Olá! Notei que não recebemos resposta. Posso ajudar com mais alguma coisa?"
																className="w-full rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
															/>
														</div>

														{/* Mensagem 2 - só aparece se quantidade >= 2 */}
														{Number.parseInt(settings.FOLLOW_UP_QUANTIDADE_MENSAGENS) >= 2 && (
															<div className="flex flex-col">
																<label className="text-xs text-grayMedium mb-1" htmlFor="FOLLOW_UP_MENSAGEM_2">Mensagem 2</label>
																<textarea
																	id="FOLLOW_UP_MENSAGEM_2"
																	name="FOLLOW_UP_MENSAGEM_2"
																	value={settings.FOLLOW_UP_MENSAGEM_2}
																	onChange={handleSettingsChange}
																	rows="2"
																	placeholder="Olá novamente! Estou disponível caso precise de alguma informação adicional."
																	className="w-full rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
																/>
															</div>
														)}

														{/* Mensagem 3 - só aparece se quantidade = 3 */}
														{Number.parseInt(settings.FOLLOW_UP_QUANTIDADE_MENSAGENS) === 3 && (
															<div className="flex flex-col">
																<label className="text-xs text-grayMedium mb-1" htmlFor="FOLLOW_UP_MENSAGEM_3">Mensagem 3</label>
																<textarea
																	id="FOLLOW_UP_MENSAGEM_3"
																	name="FOLLOW_UP_MENSAGEM_3"
																	value={settings.FOLLOW_UP_MENSAGEM_3}
																	onChange={handleSettingsChange}
																	rows="2"
																	placeholder="Só passando para ver se está tudo bem. Estou à disposição para ajudar!"
																	className="w-full rounded-lg border border-emeraldMedium/30 bg-grayBase/80 px-4 py-2 text-textOnDarkStrong focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 backdrop-blur-sm"
																/>
															</div>
														)}
													</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* TTS Settings Card */}
					<div className="mt-6">
						<TTSSection
							instanceId={instanceId}
							settings={settings}
							onSettingsChange={handleSettingsChange}
							showTTSSettings={showTTSSettings}
							toggleTTSSettings={() => setShowTTSSettings(!showTTSSettings)}
							setShowSmartAudioModal={setShowSmartAudioModal}
							setShowElevenLabsModal={setShowElevenLabsModal}
							knowledgeBaseActive={knowledgeBaseActive}
						/>
					</div>

					{/* AI Media Settings Card */}
					<div className="mt-6">
						<AIMediaSection
							instanceId={instanceId}
							showAIMediaSettings={showAIMediaSettings}
							toggleAIMediaSettings={toggleAIMediaSettings}
							settings={settings}
							knowledgeBaseActive={knowledgeBaseActive}
							onSettingsChange={handleSettingsChange}
						/>
					</div>

						{/* Groups Section */}
						<div className="mt-6">
							<GroupsSection
								instanceId={instanceId}
								settings={settings}
								onSettingsChange={handleSettingsChange}
							/>
						</div>

						{/* Botão de salvar configurações removido - agora está na barra fixa superior */}
					</form>

					{/* Group Scheduler Section - Fora do form para evitar conflito de submit */}
					<div className="mt-6">
						<GroupSchedulerSection
							instanceId={instanceId}
							settings={settings}
						/>
					</div>

					{/* Integrations Divider */}
					<div className="mt-10 mb-6 flex items-center gap-4">
						<div className="flex-1 h-px bg-gradient-to-r from-transparent via-emeraldMedium/40 to-transparent"></div>
						<span className="text-emeraldLight font-semibold text-sm tracking-wide uppercase">Integrações</span>
						<div className="flex-1 h-px bg-gradient-to-r from-transparent via-emeraldMedium/40 to-transparent"></div>
					</div>

					{/* API Functions Section */}
					<div className="mt-6">
						<APIFunctionsSection
							instanceId={instanceId}
							showAPIFunctionsSettings={showAPIFunctionsSettings}
							toggleAPIFunctionsSettings={toggleAPIFunctionsSettings}
							settings={settings}
							knowledgeBaseActive={knowledgeBaseActive}
							onSettingsChange={handleSettingsChange}
						/>
					</div>

					{/* Google Calendar Section */}
					<div className="mt-6">
						<GoogleCalendarSection
							instanceId={instanceId}
							settings={settings}
							onSettingsChange={handleSettingsChange}
							knowledgeBaseActive={knowledgeBaseActive}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

